// kernel/useInventory.ts
import { validatePlanEvidencePage } from '#lib/footfall/assetPlanEvidenceData';
import { useCallback, useRef } from 'react';
import { sourceActionFailure } from '#lib/source-actions';
import { readListCreativeContext } from '#lib/list/listCreativeContext';
import { readAttachmentDesk } from '#lib/chat/attachments';
import { readBrandRevenueCoverage } from '#lib/inventory/brands/revenueCoverage';
import {
  type KernelHandle,
  type LeasingEventType,
  type RealtimeEvent,
  type EntityType,
  type LeasingFloorDTO,
  type LeasingPropertyDTO,
  type LeasingUnitDTO,
  type LeasingBrandDTO,
  SourcingTaxonomy,
  InventoryEntityType,
  useInventoryReturnType,
  INVENTORY_ENDPOINTS,
  InventoryRegistry,
  LeasingDealDTO,
  InventoryMutationOperation,
  InventoryMutationSuccess,
  type InventoryMutationFailure,
  STUDIO_AUTOSAVE_BATCH_REASON_PREFIX,
  STUDIO_WRITE_DRAIN_LIMIT_MS,
  STUDIO_WRITE_HELD_RETRY_MS,
  STUDIO_WRITE_KEEPALIVE_BODY_LIMIT_BYTES,
  STUDIO_WRITE_REQUEST_LIMIT_MS,
  STUDIO_WRITE_RETRY_DELAYS_MS,
  STUDIO_WRITE_SUSPENSION_LIMIT_MS,
  LeasingPortfolioDTO,
  LeasingLegalEntityDTO,
  LeasingMarketSignalDTO,
  DataAuditDTO,
  SlideCorrectionDTO,
  ChatSession,
  MarketSignalStats,
  Pagination,
  LeasingLeadDTO,
  LeasingProjectDTO,
  type LeasingOrganisationDTO,
  type OrganisationTeamDTO,
  type LeasingUserDTO,
  type NotificationDTO,
  type CampaignDTO,
  type SourcingCampaignDTO,
  type PortfolioFitCampaignDTO,
  type PortfolioFitDTO,
  type InventoryRefreshOptions,
  type AutosaveState,
  type AutosaveStatus,
  type QueueAutosaveOptions,
  type KernelEntityType,
  ENTITY_TYPES,
  type ContactDTO,
  type StudioLayerDTO,
  normalizeLayerWorkspaceId,
  type StudioWorkspaceDTO,
  type StudioWorkspaceVersionDTO,
  type InventoryEntityState,
  type UniversalFactDTO,
  type TimelineEventDTO,
  type TimelineEventCategory,
  type InventoryCountableEntityType,
  INVENTORY_COUNT_GLOBAL_SCOPE_KEY,
  INVENTORY_COUNTABLE_ENTITY_TYPES,
  type InventoryCountEntry,
  type InventoryCountState,
  type InventoryStatsEntityType,
  INVENTORY_STATS_GLOBAL_SCOPE_KEY,
  INVENTORY_STATS_SUPPORTED_ENTITY_TYPES,
  type InventoryStatsEntry,
  type InventoryStatsState,
  type TableStatsResponseV1,
  CreditGrantDTO,
  type Fater3DSite,
  type StudioWorkspace,
} from '#types/types';
import { toast } from 'sonner';
import {
  handleExternalStudioLayerDtoChanges,
  type ExternalStudioLayerDtoChange,
  handleExternalStudioWorkspaceDtoChanges,
  type ExternalStudioWorkspaceDtoChange,
} from '@/kernel/useLayers';
import {
  handleExternalStudioWorkspaceVersionDtoChanges,
  type ExternalStudioWorkspaceVersionDtoChange,
} from '@/kernel/useVersions';
import { fetchApi } from './apiFetch';
import { createDaimonConversationClient } from '#lib/daimon/continuity.client';
import { normalizeWorkspaceAnnotationOpaque, resolveWorkspaceAnnotationOpaqueRollback } from '#lib/workspace-annotations';
import { type StudioWorkspaceAutosavePayload, toStudioWorkspaceAutosavePayload, toStudioWorkspaceDtoFromRuntime, toWorkspaceState } from '#lib/studio-runtime-dto';
import { applyEventToQueryCache, UPSERT_ON_GHOST_UPDATE_TYPES } from '@/kernel/inventoryQueryCache';
import { cleanRecord, deepEqual, isPlainObject, readCanonicalEntity } from '@/kernel/entityMerge';
import * as fater3d from '#lib/modelist/fater3dState';

interface useInventoryArgs {
  kernel: React.RefObject<KernelHandle | null>;
}

interface LeasingDataResponse {
  properties?: LeasingPropertyDTO[];
  floors?: LeasingFloorDTO[];
  deals?: LeasingDealDTO[];
  brands?: LeasingBrandDTO[];
  portfolios?: LeasingPortfolioDTO[];
  legalEntities?: LeasingLegalEntityDTO[];
  units?: LeasingUnitDTO[];
  signals?: LeasingMarketSignalDTO[];
  audits?: DataAuditDTO[];
  chatSessions?: ChatSession[];
  timelineEvents?: TimelineEventDTO[];
  leads?: LeasingLeadDTO[];
  campaigns?: CampaignDTO[];
  sourcingCampaigns?: SourcingCampaignDTO[];
  portfolioFitCampaigns?: PortfolioFitCampaignDTO[];
  portfolioFits?: PortfolioFitDTO[];
  projects?: LeasingProjectDTO[];
  organisations?: LeasingOrganisationDTO[];
  teams?: OrganisationTeamDTO[];
  users?: LeasingUserDTO[];
  notifications?: NotificationDTO[];
  contacts?: ContactDTO[];
  facts?: UniversalFactDTO[];
  layers?: StudioLayerDTO[];
  workspaces?: StudioWorkspaceDTO[];
  workspaceVersions?: StudioWorkspaceVersionDTO[];
  creditGrants?: CreditGrantDTO[];
  slideCorrections?: SlideCorrectionDTO[];
  stats?: MarketSignalStats;
  pagination?: Pagination <any> | null;
}

const ensureIsoString = (value: string | Date | null | undefined): string => {
  if (!value) return new Date(0).toISOString();
  if (value instanceof Date) return value.toISOString();
  return value;
};

const isSourcingTaxonomy = (value: unknown): value is SourcingTaxonomy =>
  typeof value === 'string' && Object.values(SourcingTaxonomy).includes(value as SourcingTaxonomy);

const resolveSourcingTaxonomy = (value: unknown, fallback: SourcingTaxonomy): SourcingTaxonomy =>
  isSourcingTaxonomy(value) ? value : fallback;

const DEFAULT_QUERY_TTL_MS = 60 * 1000;
const DEFAULT_ENTITY_COUNT_TTL_MS = 60 * 1000;
const DEFAULT_ENTITY_STATS_TTL_MS = 60 * 1000;
const ENTITY_TYPE_SET = new Set<EntityType>(ENTITY_TYPES);
const DEFAULT_IDS_ENSURE_BATCH_SIZE = 200;
const DEFAULT_PERSIST_SCOPE = 'default';
const DEFAULT_ENTITY_PERSIST_DEBOUNCE_MS = 900;
const DEFAULT_ENTITY_PERSIST_RETRY_DELAY_MS = 1200;
const DEFAULT_ENTITY_PERSIST_MAX_RETRIES = 1;
const STUDIO_WORKSPACE_DTO_AUTOSAVE_SCOPE = 'studio_workspace_dto';
const STUDIO_WORKSPACE_DTO_AUTOSAVE_DEBOUNCE_MS = 400;
const AUTOSAVE_MUTATION_BATCH_WINDOW_MS = 45;
const AUTOSAVE_MUTATION_BATCH_MAX_OPERATIONS = 120;
const STUDIO_WRITE_DRAIN_POLL_MS = 50;
const INVENTORY_COUNT_ENDPOINTS: Record<InventoryCountableEntityType, string> = {
  Property: '/api/inventory/properties/count',
  Unit: '/api/inventory/units/count',
  Brand: '/api/inventory/brands/count',
  Contact: '/api/inventory/contacts/count',
  Deal: '/api/inventory/deals/count',
};
const INVENTORY_GLOBAL_SCOPE_ONLY_QUERY_ENTITY_SET = new Set<InventoryEntityType>([
  'Brand',
  'LegalEntity',
  'BrandLocation',
  'LegalEntityFinancialRecord',
]);
const INVENTORY_GLOBAL_SCOPE_ONLY_COUNT_ENTITY_SET = new Set<InventoryCountableEntityType>([
  'Brand',
]);
const INVENTORY_GLOBAL_SCOPE_ONLY_STATS_ENTITY_SET = new Set<InventoryStatsEntityType>([
  'Brand',
]);
const INVENTORY_STATS_ENDPOINTS: Record<InventoryStatsEntityType, string> = {
  Audit: '/api/inventory/audits/stats',
  Brand: '/api/inventory/brands/stats',
  PortfolioFit: '/api/inventory/portfolios/portfolio-fit-stats',
};
const buildInventoryCountScopeKey = (
  organisationId?: string | null
): string => {
  const normalizedOrganisationId =
    typeof organisationId === 'string' ? organisationId.trim() : '';
  return normalizedOrganisationId || INVENTORY_COUNT_GLOBAL_SCOPE_KEY;
};
const buildInventoryStatsScopeKey = (
  organisationId?: string | null
): string => {
  const normalizedOrganisationId =
    typeof organisationId === 'string' ? organisationId.trim() : '';
  return normalizedOrganisationId || INVENTORY_STATS_GLOBAL_SCOPE_KEY;
};
const buildInventoryCountCacheKey = (
  entity: InventoryCountableEntityType,
  scopeKey: string
): string => `${entity}:${scopeKey}`;
const buildInventoryStatsCacheKey = (
  entity: InventoryStatsEntityType,
  scopeKey: string
): string => `${entity}:${scopeKey}`;
const createDefaultInventoryCountEntry = (): InventoryCountEntry => ({
  count: 0,
  isLoading: false,
  error: null,
  timestamp: 0,
});
const createDefaultInventoryStatsEntry = (): InventoryStatsEntry => ({
  data: null,
  isLoading: false,
  error: null,
  timestamp: 0,
  stale: false,
});
const createEmptyInventoryCounts = (): InventoryCountState => ({
  Property: {},
  Unit: {},
  Brand: {},
  Contact: {},
  Deal: {},
});
const createEmptyInventoryStats = (): InventoryStatsState => ({
  Audit: {},
  Brand: {},
  PortfolioFit: {},
});
const INVENTORY_STATS_SUPPORTED_ENTITY_TYPE_SET = new Set<InventoryStatsEntityType>(
  INVENTORY_STATS_SUPPORTED_ENTITY_TYPES
);
const INVENTORY_ENTITY_TO_STATS_ENTITY: Partial<Record<InventoryEntityType, InventoryStatsEntityType>> = {
  Audit: 'Audit',
  Brand: 'Brand',
};
const DETAIL_COVERAGE_FULL = 'full';
const MAX_TIMELINE_FEED_IDS = 2000;
const TIMELINE_FEED_LOAD_CATEGORIES: TimelineEventCategory[] = [
  'COMMUNICATION',
  'AGENTIC',
  'MUTATION',
];
export const TIMELINE_FEED_LOAD_LIMIT = 40;
// Matches the browser's ~6-connections-per-host ceiling: going wider just queues
// requests while multiplying in-flight state across recursion levels.
const DEPENDENCY_SYNC_CONCURRENCY = 6;

type InventoryDetailCoverage = typeof DETAIL_COVERAGE_FULL | null;
type InventoryDependencyRef = {
  type: InventoryEntityType;
  id: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasOwn = (value: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

const extractTimelineEventChatSessionId = (
  entity: InventoryEntityState<InventoryEntityType> | null
): string => {
  if (!entity || typeof entity !== 'object') return '';
  const candidate = (entity as unknown as Record<string, unknown>).chatSessionId;
  return typeof candidate === 'string' ? candidate.trim() : '';
};

const normalizeNonEmptyString = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : '';
};

const buildTimelineSubjectKey = (targetEntityType: string, targetEntityId: string): string =>
  `${targetEntityType}:${targetEntityId}`;

export const buildTimelineSessionFeedKey = (sessionId: string): string =>
  `session:${sessionId}`;

export const buildTimelineSubjectFeedKey = (subjectKey: string): string =>
  `subject:${subjectKey}`;

const extractTimelineEventTargetEntityType = (
  entity: InventoryEntityState<InventoryEntityType> | null
): string => {
  if (!entity || typeof entity !== 'object') return '';
  const record = entity as unknown as Record<string, unknown>;
  const direct = normalizeNonEmptyString(record.targetEntityType);
  if (direct) return direct;
  const payload = isRecord(record.payload) ? record.payload : null;
  return payload ? normalizeNonEmptyString(payload.entityType) : '';
};

const extractTimelineEventTargetEntityId = (
  entity: InventoryEntityState<InventoryEntityType> | null
): string => {
  if (!entity || typeof entity !== 'object') return '';
  const record = entity as unknown as Record<string, unknown>;
  const direct = normalizeNonEmptyString(record.targetEntityId);
  if (direct) return direct;
  const payload = isRecord(record.payload) ? record.payload : null;
  return payload ? normalizeNonEmptyString(payload.entityId) : '';
};

const extractTimelineEventSubjectKey = (
  entity: InventoryEntityState<InventoryEntityType> | null
): string => {
  const targetEntityType = extractTimelineEventTargetEntityType(entity);
  const targetEntityId = extractTimelineEventTargetEntityId(entity);
  if (!targetEntityType || !targetEntityId) return '';
  return buildTimelineSubjectKey(targetEntityType, targetEntityId);
};

const normalizeTimelineEventIdList = (candidate: unknown): string[] => {
  if (!Array.isArray(candidate)) return [];
  const seen = new Set<string>();
  const ids: string[] = [];
  candidate.forEach((entry) => {
    if (typeof entry !== 'string') return;
    const normalized = entry.trim();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    ids.push(normalized);
  });
  return ids;
};

const toTimelineEventTimestamp = (value: unknown): number => {
  const iso = normalizeNonEmptyString(value);
  if (!iso) return 0;
  const parsed = new Date(iso).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const compareTimelineEventsDesc = (
  left: { id: string; createdAt?: string | null } | undefined,
  right: { id: string; createdAt?: string | null } | undefined,
): number => {
  const leftTs = toTimelineEventTimestamp(left?.createdAt);
  const rightTs = toTimelineEventTimestamp(right?.createdAt);
  if (leftTs !== rightTs) return rightTs - leftTs;
  const leftId = normalizeNonEmptyString(left?.id);
  const rightId = normalizeNonEmptyString(right?.id);
  return rightId.localeCompare(leftId);
};

const sortTimelineIdsDesc = (
  ids: string[],
  registry: Record<string, InventoryEntityState<'TimelineEvent'>>,
): string[] => {
  if (ids.length <= 1) return ids;
  const order = new Map<string, number>();
  ids.forEach((id, index) => order.set(id, index));
  return [...ids].sort((leftId, rightId) => {
    const left = registry[leftId] as unknown as { id: string; createdAt?: string | null } | undefined;
    const right = registry[rightId] as unknown as { id: string; createdAt?: string | null } | undefined;
    const cmp = compareTimelineEventsDesc(left, right);
    if (cmp !== 0) return cmp;
    return (order.get(leftId) ?? 0) - (order.get(rightId) ?? 0);
  });
};

type ParsedTimelineRefs = {
  session?: {
    sessionId: string;
    ids: string[];
    hasMoreOlder: boolean;
    olderCursor: string | null;
  };
  subject?: {
    subjectKey: string;
    targetEntityType: string;
    targetEntityId: string;
    ids: string[];
    hasMoreOlder: boolean;
    olderCursor: string | null;
  };
};

const parseTimelineRefsPayload = (candidate: unknown): ParsedTimelineRefs => {
  if (!isRecord(candidate)) return {};
  const timelineRefs = isRecord(candidate.timelineRefs) ? candidate.timelineRefs : candidate;
  if (!isRecord(timelineRefs)) return {};

  const parsed: ParsedTimelineRefs = {};

  const sessionRaw = isRecord(timelineRefs.session) ? timelineRefs.session : null;
  if (sessionRaw) {
    const sessionId = normalizeNonEmptyString(sessionRaw.sessionId);
    const ids = normalizeTimelineEventIdList(sessionRaw.ids);
    if (sessionId) {
      parsed.session = {
        sessionId,
        ids,
        hasMoreOlder: sessionRaw.hasMoreOlder === true,
        olderCursor: normalizeNonEmptyString(sessionRaw.olderCursor) || null,
      };
    }
  }

  const subjectRaw = isRecord(timelineRefs.subject) ? timelineRefs.subject : null;
  if (subjectRaw) {
    const targetEntityType = normalizeNonEmptyString(subjectRaw.targetEntityType);
    const targetEntityId = normalizeNonEmptyString(subjectRaw.targetEntityId);
    const ids = normalizeTimelineEventIdList(subjectRaw.ids);
    if (targetEntityType && targetEntityId) {
      parsed.subject = {
        subjectKey: buildTimelineSubjectKey(targetEntityType, targetEntityId),
        targetEntityType,
        targetEntityId,
        ids,
        hasMoreOlder: subjectRaw.hasMoreOlder === true,
        olderCursor: normalizeNonEmptyString(subjectRaw.olderCursor) || null,
      };
    }
  }

  return parsed;
};

const dedupeArrayById = (entries: unknown[]): unknown[] => {
  const seen = new Set<string>();
  return entries.filter((entry) => {
    if (!isRecord(entry)) return false;
    const id = typeof entry.id === 'string' ? entry.id.trim() : '';
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

type NormalizeIncomingEntityOptions = {
  entityId?: string;
  existingEntity?: Record<string, unknown> | null;
  preserveMissingWorkspaceAnnotationOpaque?: boolean;
};

type InventoryExternalEventSource = 'socket';

type InventoryExternalDtoChange = {
  entityType: InventoryEntityType;
  entityId: string;
  eventType: LeasingEventType;
  previousEntity: InventoryEntityState<InventoryEntityType> | null;
  nextEntity: InventoryEntityState<InventoryEntityType> | null;
  event: RealtimeEvent;
};

type InventoryExternalEntityExtensionContext = {
  kernel: React.RefObject<KernelHandle | null>;
  source: InventoryExternalEventSource;
  entityType: InventoryEntityType;
  changes: InventoryExternalDtoChange[];
};

type InventoryExternalEntityExtension = (context: InventoryExternalEntityExtensionContext) => void;

type InventoryExternalEntityChangesMap = Map<
  InventoryEntityType,
  InventoryExternalDtoChange[]
>;

const INVENTORY_EXTERNAL_ENTITY_EXTENSIONS: Partial<Record<InventoryEntityType, InventoryExternalEntityExtension>> = {
  TimelineEvent: ({ kernel, changes }) => {
    const kernelHandle = kernel.current;
    if (!kernelHandle || typeof kernelHandle.syncChatSessionMessagesFromTimeline !== 'function') return;

    const touchedSessionIds = new Set<string>();
    changes.forEach((change) => {
      const previousSessionId = extractTimelineEventChatSessionId(change.previousEntity);
      const nextSessionId = extractTimelineEventChatSessionId(change.nextEntity);
      if (previousSessionId) touchedSessionIds.add(previousSessionId);
      if (nextSessionId) touchedSessionIds.add(nextSessionId);
    });

    touchedSessionIds.forEach((sessionId) => {
      kernelHandle.syncChatSessionMessagesFromTimeline(sessionId);
    });
  },
  StudioLayer: ({ kernel, source, changes }) => {
    if (source !== 'socket') return;
    const layerChanges: ExternalStudioLayerDtoChange[] = changes.map((change) => ({
      entityId: change.entityId,
      eventType: change.eventType,
      previousEntity: change.previousEntity as ExternalStudioLayerDtoChange['previousEntity'],
      nextEntity: change.nextEntity as ExternalStudioLayerDtoChange['nextEntity'],
    }));
    handleExternalStudioLayerDtoChanges({
      kernel,
      changes: layerChanges,
    });
  },
  StudioWorkspace: ({ kernel, source, changes }) => {
    if (source !== 'socket') return;
    const workspaceChanges: ExternalStudioWorkspaceDtoChange[] = changes.map((change) => ({
      entityId: change.entityId,
      eventType: change.eventType,
      previousEntity: change.previousEntity as ExternalStudioWorkspaceDtoChange['previousEntity'],
      nextEntity: change.nextEntity as ExternalStudioWorkspaceDtoChange['nextEntity'],
    }));
    handleExternalStudioWorkspaceDtoChanges({
      kernel,
      changes: workspaceChanges,
    });
  },
  StudioWorkspaceVersion: ({ kernel, source, changes }) => {
    if (source !== 'socket') return;
    const workspaceVersionChanges: ExternalStudioWorkspaceVersionDtoChange[] = changes.map((change) => ({
      entityId: change.entityId,
      eventType: change.eventType,
      previousEntity: change.previousEntity as ExternalStudioWorkspaceVersionDtoChange['previousEntity'],
      nextEntity: change.nextEntity as ExternalStudioWorkspaceVersionDtoChange['nextEntity'],
    }));
    handleExternalStudioWorkspaceVersionDtoChanges({
      kernel,
      changes: workspaceVersionChanges,
    });
  },
};

const hasInventoryExternalEntityExtension = (entityType: InventoryEntityType): boolean =>
  Boolean(INVENTORY_EXTERNAL_ENTITY_EXTENSIONS[entityType]);

const dispatchInventoryExternalEntityExtensions = ({
  kernel,
  source,
  changesByEntityType,
}: {
  kernel: React.RefObject<KernelHandle | null>;
  source: InventoryExternalEventSource;
  changesByEntityType: InventoryExternalEntityChangesMap;
}): void => {
  changesByEntityType.forEach((changes, entityType) => {
    if (changes.length === 0) return;
    const extension = INVENTORY_EXTERNAL_ENTITY_EXTENSIONS[entityType];
    if (!extension) return;
    try {
      extension({
        kernel,
        source,
        entityType,
        changes,
      });
    } catch (error) {
      console.error(`[Inventory Extensions] External DTO extension failed for ${entityType}.`, error);
    }
  });
};

const mergeRuntimeOverlay = (
  existingEntity: Record<string, unknown> | null,
  incomingEntity: Record<string, unknown>
): Record<string, unknown> => {
  const existingRuntime = existingEntity && isRecord(existingEntity.runtime)
    ? existingEntity.runtime
    : {};
  const incomingRuntime = isRecord(incomingEntity.runtime)
    ? incomingEntity.runtime
    : {};
  return {
    ...existingRuntime,
    ...incomingRuntime,
  };
};

const hoistRuntimeToRoot = (
  entity: Record<string, unknown>,
  runtime: Record<string, unknown>
): Record<string, unknown> => ({
  ...entity,
  ...runtime,
});

export const normalizeIncomingEntity = <K extends InventoryEntityType>(
  type: K,
  incoming: unknown,
  options?: NormalizeIncomingEntityOptions,
): InventoryEntityState<K> | null => {
  if (!isRecord(incoming)) return null;

  const fallbackId = typeof options?.entityId === 'string' ? options.entityId.trim() : '';
  const incomingId = typeof incoming.id === 'string' ? incoming.id.trim() : '';
  const entityId = incomingId || fallbackId;
  if (!entityId) return null;

  const existingEntity = options?.existingEntity ?? null;
  const normalized: Record<string, unknown> = { ...incoming, id: entityId };
  if (type === 'Notification' && existingEntity) {
    const previousTime = Date.parse(String(existingEntity.updatedAt ?? existingEntity.createdAt)) || 0;
    const incomingTime = Date.parse(String(normalized.updatedAt ?? normalized.createdAt)) || 0;
    if (previousTime > incomingTime) return existingEntity as unknown as InventoryEntityState<K>;
  }

  // Pre-existing on preprod, inherited here because a Slides commit changed this
  // function's signature and the gate lints CHANGED files. The switch has a
  // `default` that returns the normalized entity, which is the correct handling
  // for every type without special normalisation; enumerating nineteen entity
  // types that all want the default adds churn to the inventory normaliser
  // without adding safety. Left as debt rather than fixed inside a Slides merge.
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (type) {
    case 'Property': {
      if (hasOwn(normalized, 'sourcingTaxonomy')) {
        normalized.sourcingTaxonomy = resolveSourcingTaxonomy(
          normalized.sourcingTaxonomy,
          SourcingTaxonomy.FATER_CATEGORIES
        );
      }
      if (Array.isArray(normalized.units)) {
        normalized.units = dedupeArrayById(normalized.units);
      }
      return normalized as unknown as InventoryEntityState<K>;
    }
    case 'Deal':
    case 'Campaign':
    case 'SourcingCampaign':
    case 'TimelineEvent':
    case 'Notification': {
      if (hasOwn(normalized, 'createdAt')) {
        normalized.createdAt = ensureIsoString(normalized.createdAt as string | Date | null | undefined);
      }
      if (hasOwn(normalized, 'expiresAt')) {
        normalized.expiresAt = normalized.expiresAt
          ? ensureIsoString(normalized.expiresAt as string | Date | null | undefined)
          : null;
      }
      if (hasOwn(normalized, 'updatedAt')) {
        normalized.updatedAt = ensureIsoString(normalized.updatedAt as string | Date | null | undefined);
      }
      if (type === 'TimelineEvent' && hasOwn(normalized, 'payload')) {
        const payload = normalized.payload;
        normalized.payload =
          payload && typeof payload === 'object' && !Array.isArray(payload)
            ? payload
            : {};
      }
      return normalized as unknown as InventoryEntityState<K>;
    }
    case 'ChatSession': {
      const mergedRuntime = mergeRuntimeOverlay(existingEntity, normalized);
      const normalizedSession: Record<string, unknown> = {
        ...normalized,
        runtime: mergedRuntime,
      };

      if (hasOwn(normalizedSession, 'messages')) {
        delete normalizedSession.messages;
      }

      if (hasOwn(normalizedSession, 'messageCount')) {
        const messageCount = normalizedSession.messageCount;
        if (!(typeof messageCount === 'number' && Number.isFinite(messageCount))) {
          normalizedSession.messageCount = 0;
        }
      }

      if (hasOwn(normalizedSession, 'contextSummary')) {
        normalizedSession.contextSummary = Array.isArray(normalizedSession.contextSummary)
          ? normalizedSession.contextSummary
          : undefined;
      }
      if (hasOwn(normalizedSession, 'workingMemory')) {
        const rawWorkingMemory =
          normalizedSession.workingMemory &&
          typeof normalizedSession.workingMemory === 'object' &&
          !Array.isArray(normalizedSession.workingMemory)
            ? (normalizedSession.workingMemory as Record<string, unknown>)
            : null;
        const rawAnchors = Array.isArray(rawWorkingMemory?.anchors)
          ? rawWorkingMemory.anchors
          : [];
        const normalizedAnchors = rawAnchors
          .filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null)
          .map((entry) => {
            const entityType = typeof entry.entityType === 'string' ? entry.entityType.trim() : '';
            const entityId = typeof entry.entityId === 'string' ? entry.entityId.trim() : '';
            if (!entityType || !entityId) return null;
            return {
              ...entry,
              entityType,
              entityId,
              mass:
                typeof entry.mass === 'number' && Number.isFinite(entry.mass)
                  ? Math.max(1, Math.floor(entry.mass))
                  : 1,
              lastAccessedAt:
                typeof entry.lastAccessedAt === 'number' && Number.isFinite(entry.lastAccessedAt)
                  ? Math.floor(entry.lastAccessedAt)
                  : Date.now(),
            };
          })
          .filter((entry) => entry !== null);
        const listCreativeContext = readListCreativeContext(rawWorkingMemory?.listCreativeContext);
        const originMasterSessionId = typeof rawWorkingMemory?.originMasterSessionId === 'string' ? rawWorkingMemory.originMasterSessionId : undefined;
        const attachments = readAttachmentDesk(rawWorkingMemory?.attachments);
        normalizedSession.workingMemory = { anchors: normalizedAnchors, ...(listCreativeContext ? { listCreativeContext } : {}), ...(originMasterSessionId ? { originMasterSessionId } : {}), ...(attachments ? { attachments } : {}) };
      }
      if (hasOwn(normalizedSession, 'subjectHistory')) {
        normalizedSession.subjectHistory = Array.isArray(normalizedSession.subjectHistory)
          ? normalizedSession.subjectHistory
          : undefined;
      }
      if (hasOwn(normalizedSession, 'lastMessageSnippet')) {
        normalizedSession.lastMessageSnippet =
          typeof normalizedSession.lastMessageSnippet === 'string'
            ? normalizedSession.lastMessageSnippet
            : null;
      }
      if (hasOwn(normalizedSession, 'strategicPlan')) {
        normalizedSession.strategicPlan = normalizedSession.strategicPlan ?? null;
      }
      if (hasOwn(normalizedSession, 'ownerId')) {
        normalizedSession.ownerId =
          typeof normalizedSession.ownerId === 'string' ? normalizedSession.ownerId : null;
      }
      if (hasOwn(normalizedSession, 'collaborationMode')) {
        const value = normalizedSession.collaborationMode;
        normalizedSession.collaborationMode =
          value === 'ASSISTED' || value === 'COLLABORATIVE' || value === 'AUTONOMOUS'
            ? value
            : 'ASSISTED';
      }
      if (hasOwn(normalizedSession, 'createdAt')) {
        normalizedSession.createdAt = ensureIsoString(
          normalizedSession.createdAt as string | Date | null | undefined
        );
      }
      if (hasOwn(normalizedSession, 'updatedAt')) {
        normalizedSession.updatedAt = ensureIsoString(
          normalizedSession.updatedAt as string | Date | null | undefined
        );
      }

      // activeSubject is a FRONTEND runtime focus, set only via setActiveSubject (which writes the
      // registry directly, not through this normalizer). A backend socket/API payload must NEVER
      // wipe the user's current subject by carrying activeSubject: null, that dropped the whole
      // Source view to a blank "plain conversation" a few minutes after launch, when a
      // campaign-completion ChatSession update arrived with activeSubject null. Preserve it on ANY
      // incoming path (this used to be gated on an option only the detail-sync path passed, the
      // socket hydrate path in updateSlice did not, hence the bug).
      {
        const existingActiveSubject = (existingEntity as { activeSubject?: unknown } | null)?.activeSubject;
        const incomingHasActiveSubject = hasOwn(normalized, 'activeSubject');
        if (existingActiveSubject && incomingHasActiveSubject && normalized.activeSubject == null) {
          normalizedSession.activeSubject = existingActiveSubject;
        }
      }

      return hoistRuntimeToRoot(
        normalizedSession,
        mergedRuntime as unknown as Record<string, unknown>
      ) as unknown as InventoryEntityState<K>;
    }
    case 'Project':
    case 'StudioWorkspace':
    case 'StudioLayer': {
      if (type === 'StudioWorkspace') {
        if (hasOwn(normalized, 'annotationOpaque')) {
          normalized.annotationOpaque = normalizeWorkspaceAnnotationOpaque(normalized.annotationOpaque);
        } else if (!options?.preserveMissingWorkspaceAnnotationOpaque) {
          normalized.annotationOpaque = false;
        }
      }
      if (type === 'StudioLayer') {
        if (hasOwn(normalized, 'projectId')) {
          if (normalized.projectId === undefined) {
            delete normalized.projectId;
          } else if (typeof normalized.projectId === 'string') {
            normalized.projectId = normalized.projectId.trim();
          }
        }
        if (hasOwn(normalized, 'workspaceId')) {
          if (normalized.workspaceId === undefined) {
            delete normalized.workspaceId;
          } else {
            normalized.workspaceId = normalizeLayerWorkspaceId(normalized.workspaceId);
          }
        }
      }
      const mergedRuntime = mergeRuntimeOverlay(existingEntity, normalized);
      const withRuntime = {
        ...normalized,
        runtime: mergedRuntime,
      };
      return hoistRuntimeToRoot(withRuntime, mergedRuntime) as unknown as InventoryEntityState<K>;
    }
    default:
      return normalized as unknown as InventoryEntityState<K>;
  }
};

const normalizePersistScope = (scope?: string | null): string => {
  const normalized = typeof scope === 'string' ? scope.trim() : '';
  return normalized.length > 0 ? normalized : DEFAULT_PERSIST_SCOPE;
};

const buildPersistEntryKey = (
  entityType: KernelEntityType,
  entityId: string,
  scope: string,
): string => `${entityType}:${entityId}:${scope}`;

const createDefaultAutosaveState = (): AutosaveState => ({
  dirty: false,
  saveState: 'idle',
  lastDraftSignature: null,
  lastPersistedSignature: null,
  retryCount: 0,
  lastError: null,
  updatedAt: 0,
});

interface AutosaveQueueEntry {
  entityType: KernelEntityType;
  entityId: string;
  scope: string;
  reason: string;
  debounceMs: number;
  maxRetries: number;
  retryDelayMs: number;
  buildPayload: () => unknown;
  serializePayload: (payload: unknown) => string;
  persist: QueueAutosaveOptions<unknown>['persist'];
  mergePending: QueueAutosaveOptions<unknown>['mergePending'];
  fieldDeltas: boolean;
  timer: ReturnType<typeof setTimeout> | null;
  inFlight: boolean;
  // Settles when the save in flight does.
  landed: Promise<void>;
  pendingPayload: unknown;
  pendingSignature: string | null;
}

// A value the server rewrites (normalization, server-owned field) never shows as it was sent: this
// bounds how long such a send keeps protecting a draft key. Measured p99 round trip is ~2.3 s.
const SENT_AUTOSAVE_OBSERVATION_TIMEOUT_MS = 15_000;

interface SentAutosaveRecord {
  values: Record<string, unknown>;
  settledAt: number | null;
}

const buildSentRecordsKey = (entityType: KernelEntityType, entityId: string): string =>
  `${entityType}:${entityId}`;

// Only the keys the row carries: a key it lacks is never pruned, so recording it protects nothing.
const toSentAutosaveRecord = (
  payload: unknown,
  canonical: Record<string, unknown> | null,
): SentAutosaveRecord | null => {
  if (!canonical || !isPlainObject(payload)) return null;
  const values = cleanRecord(Object.fromEntries(Object.entries(payload).filter(([key]) => hasOwn(canonical, key))));
  return Object.keys(values).length > 0 ? { values, settledAt: null } : null;
};

// A key is matched only against the oldest record still waiting on it: a later send of the value the
// row held before an earlier send would otherwise take that untouched row for its own answer.
// The clock check backs the bound timer, which a throttled background tab can fire long after it.
const releaseObservedSentValues = (
  records: SentAutosaveRecord[],
  canonical: Record<string, unknown> | null,
  now: number,
): SentAutosaveRecord[] => {
  const waitingKeys = new Set<string>();
  return records.filter((record) => {
    if (record.settledAt !== null && now - record.settledAt >= SENT_AUTOSAVE_OBSERVATION_TIMEOUT_MS) return false;
    Object.keys(record.values).forEach((key) => {
      if (!waitingKeys.has(key) && canonical && hasOwn(canonical, key) && deepEqual(record.values[key], canonical[key])) {
        delete record.values[key];
        return;
      }
      waitingKeys.add(key);
    });
    return Object.keys(record.values).length > 0;
  });
};

interface AutosaveMutationBatchItem {
  operations: InventoryMutationOperation[];
  resolve: (results: InventoryMutationSuccess[]) => void;
  reject: (error: unknown) => void;
  // Faults the server pinned on one of this entry's operations, over all its sends.
  faults?: number;
  // Sent again once behind its workspace's create, the server having never received that workspace.
  followedMissingWorkspace?: boolean;
}

// The refusal of a save whose workspace the server has no row for (app/api/inventory/mutate).
const missingWorkspaceIdOf = (error: unknown): string | null =>
  readMutationFailure(error)?.refused === true
    ? /^Payload workspaceId (\S+) matches no workspace row\.$/.exec(readMutationFailure(error)?.message ?? '')?.[1] ?? null
    : null;

// Past the retry delays the queue stops waiting in line: any new write, the network coming back or
// the periodic try sends it again.
const isStudioWriteQueueHeld = (stalls: number): boolean => stalls > STUDIO_WRITE_RETRY_DELAYS_MS.length;

// How many entries from the head one request takes. One request is one server transaction: a queue
// that grew while held leaves in several. An entry is never split, so one larger than the cap goes
// alone. Under a byte budget, 0 when the head alone exceeds it.
const countStudioWriteBatch = (queue: AutosaveMutationBatchItem[], byteBudget?: number): number => {
  let entryCount = 0;
  let operationCount = 0;
  let byteCount = 0;
  for (const entry of queue) {
    operationCount += entry.operations.length;
    if (entryCount > 0 && operationCount > AUTOSAVE_MUTATION_BATCH_MAX_OPERATIONS) break;
    if (byteBudget !== undefined) {
      byteCount += new Blob([JSON.stringify(entry.operations)]).size;
      if (byteCount > byteBudget) break;
    }
    entryCount += 1;
  }
  return entryCount;
};

// Stays up for as long as the writes are not leaving: a notice that fades leaves unsaved work looking saved.
const raiseStudioWriteHeldNotice = (k: KernelHandle | null): void => {
  k?.handleError('Your changes are not being saved yet. We keep trying.', { id: 'studio-write-held', duration: Infinity });
};

const isUnsentStudioAutosave = (entry: AutosaveQueueEntry): boolean =>
  hasOwn(STUDIO_AUTOSAVE_ENTITY_LABELS, entry.entityType) && entry.pendingPayload !== null && entry.pendingPayload !== undefined;

const readMutationFailure = (error: unknown): Partial<InventoryMutationFailure> | undefined =>
  (error as { mutationFailure?: Partial<InventoryMutationFailure> } | null)?.mutationFailure;

// Marks the error the queue rejects a save with when it gave that save up: sent again as is, the save would fail again.
const isSetAsideStudioWrite = (error: unknown): boolean =>
  (error as { studioWriteSetAside?: boolean } | null)?.studioWriteSetAside === true;

// The failure names an operation of the merged request, unless the server says the fault was not
// that operation's; a save is set aside whole, never one operation of it.
const findFailedAutosaveBatchEntry = (
  batch: AutosaveMutationBatchItem[],
  error: unknown,
): AutosaveMutationBatchItem | null => {
  const failure = readMutationFailure(error);
  if (failure?.transient === true || typeof failure?.index !== 'number' || failure.index < 0) return null;
  let offset = failure.index;
  for (const entry of batch) {
    if (offset < entry.operations.length) return entry;
    offset -= entry.operations.length;
  }
  return null;
};

const STUDIO_AUTOSAVE_ENTITY_LABELS: Record<string, string> = {
  StudioLayer: 'layer',
  StudioWorkspace: 'workspace',
  StudioWorkspaceVersion: 'version',
};

const isKnownEntityType = (value: InventoryEntityType | string): value is EntityType =>
  ENTITY_TYPE_SET.has(value as EntityType);

const stableStringify = (value: unknown): string => {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return `[${value.map((entry) => stableStringify(entry)).join(',')}]`;
  if (value instanceof Date) return JSON.stringify(value.toISOString());
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => `${JSON.stringify(key)}:${stableStringify(entry)}`);
    return `{${entries.join(',')}}`;
  }
  return JSON.stringify(value);
};

const normalizeQueryValue = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value)) {
    const normalized = value
      .map((entry) => normalizeQueryValue(entry))
      .filter((entry): entry is string => entry !== null)
      .sort((left, right) => left.localeCompare(right));
    return normalized.length > 0 ? normalized.join(',') : null;
  }
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') return stableStringify(value);
  return String(value);
};

const computeQuerySignature = (
  entity: InventoryEntityType,
  filters: Record<string, unknown> = {},
  pagination?: { limit: number; offset: number }
): string => {
  const parts: string[] = [];
  Object.keys(filters)
    .sort((left, right) => left.localeCompare(right))
    .forEach((key) => {
      if (key === 'focusIds' && filters.ids !== undefined && filters.ids !== null) return;
      const normalized = normalizeQueryValue(filters[key]);
      if (normalized === null) return;
      const queryKey = key === 'focusIds' ? 'ids' : key;
      parts.push(`${encodeURIComponent(queryKey)}=${encodeURIComponent(normalized)}`);
    });

  if (pagination) {
    parts.push(`limit=${Math.max(1, pagination.limit)}`);
    parts.push(`offset=${Math.max(0, pagination.offset)}`);
  }

  return `${entity}?${parts.join('&')}`;
};

const chunkStringArray = (items: string[], size: number): string[][] => {
  const safeSize = Math.max(1, Math.floor(size));
  const chunks: string[][] = [];
  for (let cursor = 0; cursor < items.length; cursor += safeSize) {
    chunks.push(items.slice(cursor, cursor + safeSize));
  }
  return chunks;
};

const isAbortError = (error: unknown): boolean =>
  Boolean(
    error &&
      typeof error === 'object' &&
      (
        (error as { name?: unknown }).name === 'AbortError' ||
        (error as { code?: unknown }).code === 'ABORT_ERR'
      )
  );

const parseInventoryDependencies = (candidate: unknown): InventoryDependencyRef[] => {
  if (!Array.isArray(candidate)) return [];
  const dependencies: InventoryDependencyRef[] = [];
  const dedupe = new Set<string>();

  candidate.forEach((entry) => {
    if (!isRecord(entry)) return;
    const typeCandidate = typeof entry.type === 'string' ? entry.type.trim() : '';
    const idCandidate = typeof entry.id === 'string' ? entry.id.trim() : '';
    if (!typeCandidate || !idCandidate) return;
    if (!Object.prototype.hasOwnProperty.call(INVENTORY_ENDPOINTS, typeCandidate)) return;
    const type = typeCandidate as InventoryEntityType;
    const dedupeKey = `${type}:${idCandidate}`;
    if (dedupe.has(dedupeKey)) return;
    dedupe.add(dedupeKey);
    dependencies.push({ type, id: idCandidate });
  });

  return dependencies;
};

const parseDetailResponsePayload = (
  payload: unknown
): { entity: unknown; dependencies: InventoryDependencyRef[]; timelineRefs?: ParsedTimelineRefs } => {
  if (!isRecord(payload) || !hasOwn(payload, 'entity')) {
    return { entity: payload, dependencies: [] };
  }
  const timelineRefs = parseTimelineRefsPayload(payload.timelineRefs);
  return {
    entity: payload.entity,
    dependencies: parseInventoryDependencies(payload.dependencies),
    timelineRefs: Object.keys(timelineRefs).length > 0 ? timelineRefs : undefined,
  };
};

export const useInventory = ({ kernel }: useInventoryArgs): useInventoryReturnType  => {
  const daimonConversations = useRef<ReturnType<typeof createDaimonConversationClient> | null>(null);
  if (!daimonConversations.current) daimonConversations.current = createDaimonConversationClient(() => kernel.current?.getUserContext().dbUserId);
  const requestDaimonConversation: useInventoryReturnType['requestDaimonConversation'] = useCallback(request => daimonConversations.current!(request), []);
  const requestDaimonTool: useInventoryReturnType['requestDaimonTool'] = useCallback((request, signal) => fetch('/api/daimon/tools', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request), signal }), []);
  // Fater 3D's sites and their Modelist runs (app/api/modelist/route.ts).
  const requestModelist: useInventoryReturnType['requestModelist'] = useCallback((request) => (request.action === 'sites' || request.action === 'read' || request.action === 'scene'
    ? fetch(`/api/modelist${request.action === 'read' ? `?sessionId=${encodeURIComponent(request.sessionId)}&since=${request.since ?? 0}` : request.action === 'scene' ? `?projectId=${encodeURIComponent(request.projectId)}` : `?${new URLSearchParams(Object.entries({ organisationId: request.organisationId, userId: request.userId }).filter((entry): entry is [string, string] => !!entry[1]))}`}`, { cache: 'no-store' })
    : fetch('/api/modelist', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request) })), []);
  // Files (app/api/share): shared documents, their links and visits.
  const requestShare: useInventoryReturnType['requestShare'] = useCallback((request) => (request.action === 'documents' ? fetch('/api/share', { cache: 'no-store' })
    : request.action === 'document' ? fetch(`/api/share?document=${encodeURIComponent(request.document)}`, { cache: 'no-store' })
      : fetch('/api/share', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request) })), []);
  // Signatures (app/api/sign): PDFs sent for signature.
  const requestSign: useInventoryReturnType['requestSign'] = useCallback((request) => (request.action === 'envelopes' ? fetch('/api/sign', { cache: 'no-store' })
    : request.action === 'envelope' ? fetch(`/api/sign?envelope=${encodeURIComponent(request.envelope)}`, { cache: 'no-store' })
      : fetch('/api/sign', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request) })), []);
  // The Video tool's films (app/api/video/route.ts); a dropped file goes to its upload route as is.
  const requestVideo: useInventoryReturnType['requestVideo'] = useCallback((request) => (request.action === 'films' ? fetch('/api/video', { cache: 'no-store' })
    : request.action === 'film' ? fetch(`/api/video?film=${encodeURIComponent(request.film)}&since=${request.since ?? 0}`, { cache: 'no-store' })
      : request.action === 'upload' ? fetch(`/api/video/upload?film=${encodeURIComponent(request.film)}&name=${encodeURIComponent(request.file.name)}`, { method: 'POST', body: request.file })
        : fetch('/api/video', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request) })), []);
  const loadFater3DSites: useInventoryReturnType['loadFater3DSites'] = useCallback(async () => {
    const filters = kernel.current?.getFater3d().filters ?? {};
    const user = kernel.current?.getUserContext();
    // As the Studio's projects dashboard (ProjectsGrid): anyone but a Fater admin is pinned to their own organisation.
    const res = await requestModelist({ action: 'sites', organisationId: filters.organisationId ?? (user?.isFaterAdmin ? undefined : user?.organisationId ?? undefined), userId: filters.userId });
    const listed = res.ok ? await res.json() as { sites: Fater3DSite[]; bimeurAvailable?: boolean } : null;
    // An answer for filters changed since it was asked is not this list.
    if (kernel.current?.getFater3d().filters !== filters) return;
    kernel.current?.setFater3d((f) => (listed ? { ...fater3d.setSites(f, listed.sites), bimeurAvailable: listed.bimeurAvailable } : { ...f, sitesLoaded: true, error: 'The projects could not be loaded.' }));
  }, [kernel, requestModelist]);
  const renameFater3DSite: useInventoryReturnType['renameFater3DSite'] = useCallback(async (sessionId, name) => {
    kernel.current?.setFater3d((f) => fater3d.setSites(f, f.sites.map((site) => (site.id === sessionId ? { ...site, name } : site))));
    const res = await requestModelist({ action: 'rename', sessionId, name }).catch(() => null);
    if (res?.ok) return;
    kernel.current?.setFater3d((f) => ({ ...f, error: 'The project could not be renamed.' }));
    void loadFater3DSites();
  }, [kernel, requestModelist, loadFater3DSites]);
  const deleteFater3DSite: useInventoryReturnType['deleteFater3DSite'] = useCallback(async (sessionId) => {
    kernel.current?.setFater3d((f) => fater3d.setSites(f, f.sites.filter((site) => site.id !== sessionId)));
    try {
      await kernel.current?.mutateInventory([{ action: 'DELETE', entity: 'ChatSession', id: sessionId }], `Delete 3D project ${sessionId}`);
    } catch (error) {
      kernel.current?.handleError(fater3d.deletionFailure(error, 'The project could not be deleted.'));
      void loadFater3DSites();
    }
  }, [kernel, loadFater3DSites]);
  const readPropertyTrafficEvidence: useInventoryReturnType['readPropertyTrafficEvidence'] = useCallback(async (propertyId, auditId, dataset, page) => {
    if (![propertyId, auditId].every(value => typeof value === 'string' && /^[a-zA-Z0-9_-]+$/.test(value)) || !['indoor', 'directional', 'attendance'].includes(dataset)) throw new Error('Invalid traffic evidence selector.');
    if (page) validatePlanEvidencePage(dataset, page);
    const requester = kernel.current;
    const user = requester?.getUserContext();
    const owner = user ? [user.dbUserId, user.organisationId, user.authenticatedOrganisationId].join('|') : null;
    if (!user?.dbUserId) throw new Error('Sign in to read traffic evidence.');
    const response = await fetchApi(`/api/inventory/properties/${encodeURIComponent(propertyId)}/traffic-evidence?auditId=${encodeURIComponent(auditId)}&dataset=${dataset}${page ? `&collection=${encodeURIComponent(page.collection)}&offset=${page.offset}&limit=${page.limit}` : ''}`, { cache: 'no-store' });
    if (!response.ok) throw new Error(`Traffic evidence unavailable (HTTP ${response.status}).`);
    const body = await response.json();
    const currentUser = kernel.current?.getUserContext();
    if (kernel.current !== requester || !currentUser || [currentUser.dbUserId, currentUser.organisationId, currentUser.authenticatedOrganisationId].join('|') !== owner) throw new Error('Traffic evidence reader identity changed.');
    const schema = { indoor: 'fater-traffic-indoor-coverage-v1', directional: 'fater-traffic-directional-commuting-v1', attendance: 'fater-attendance-references-v1' }[dataset];
    const identityMatches = body && body.descriptor?.propertyId === propertyId && body.originalTextSha256 === body.descriptor?.datasets?.[dataset]?.originalTextSha256 && body.originalTextBytes === body.descriptor?.datasets?.[dataset]?.originalTextBytes && body.propertyId === propertyId && body.auditId === auditId && body.dataset === dataset && typeof body.revision === 'string' && body.descriptor?.auditId === auditId && body.descriptor?.revision === body.revision;
    if (!identityMatches) throw new Error('Traffic evidence response identity mismatch.');
    if (page) {
      if (body.schema !== 'fater-plan-traffic-evidence-page-v1' || body.organisationId !== user.organisationId || body.packSchema !== schema || body.collection !== page.collection || body.offset !== page.offset || body.requestedLimit !== page.limit || !Array.isArray(body.records) || body.returned !== body.records.length || !Number.isSafeInteger(body.total) || body.total < 0 || body.returned > page.limit || body.returned > Math.max(0, body.total - page.offset) || typeof body.byteLimited !== 'boolean' || body.nextOffset !== (page.offset + body.returned < body.total ? page.offset + body.returned : null) || (!body.returned && page.offset < body.total) || body.pack !== undefined || new TextEncoder().encode(JSON.stringify(body)).byteLength > 24 * 1024) throw new Error('Traffic evidence page identity mismatch.');
    } else if (body.schema !== 'fater-plan-traffic-evidence-response-v1' || body.pack?.schema !== schema || body.pack?.organisationId !== user.organisationId || body.pack?.propertyId !== propertyId) throw new Error('Traffic evidence response identity mismatch.');
    return body;
  }, [kernel]);
  const inflightRefreshesRef = useRef<Map<string, Promise<void>>>(new Map());
  const inflightQueriesRef = useRef<Map<string, Promise<{ ids: string[]; pagination: Pagination<any> | null; error: string | null }>>>(new Map());
  const inflightDetailFetchesRef = useRef<Map<string, Promise<any>>>(new Map());
  const inflightCountFetchesRef = useRef<Map<string, Promise<void>>>(new Map());
  const inflightStatsFetchesRef = useRef<Map<string, Promise<void>>>(new Map());
  const inflightRefreshAbortRef = useRef<Map<string, AbortController>>(new Map());
  const inflightQueriesAbortRef = useRef<Map<string, AbortController>>(new Map());
  const inflightDetailAbortRef = useRef<Map<string, AbortController>>(new Map());
  const autosaveQueueRef = useRef<Map<string, AutosaveQueueEntry>>(new Map());
  const sentAutosaveRecordsRef = useRef<Map<string, SentAutosaveRecord[]>>(new Map());
  // Outlives the sent records: a value the row never shows must not be sent again once its record is released.
  // Holds what a creation wrote too, which its creator sends itself.
  const lastSentFieldValuesRef = useRef<Map<string, Record<string, unknown>>>(new Map());
  const autosaveMutationBatchQueueRef = useRef<AutosaveMutationBatchItem[]>([]);
  // Workspaces created again after the server refused their saves for want of a row: once each.
  const recreatedWorkspaceIdsRef = useRef<Set<string>>(new Set());
  const autosaveMutationBatchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autosaveMutationBatchInFlightRef = useRef(false);
  // Consecutive faults naming no operation, since the server last answered.
  const autosaveMutationBatchStallsRef = useRef(0);
  // True for the synchronous span of sendStudioWritesAtLeave: a request opened inside it outlives the page.
  const studioWriteLeaveRef = useRef(false);
  // Saves preparing their payload before queueStudioWrite (a layer's pixels uploading): nothing of them is queued yet.
  const studioWritesPreparingRef = useRef(0);
  // When set, an identity switch is under way since then: writes keep queueing and none is sent,
  // whatever asks. Set by runIdentitySwitch, lifted by it when the switch did not happen, and by
  // clearInventory when it did.
  const studioWriteSuspendedSinceRef = useRef<number | null>(null);
  const clearEpochRef = useRef(0);
  const echoWatchesRef = useRef(new Set<Set<string>>());

  // The `${entity}:${id}` rows realtime events write while the watch is open, for a read that must not undo them.
  const watchInventoryEchoes = useCallback(() => {
    const echoed = new Set<string>();
    echoWatchesRef.current.add(echoed);
    return { echoed: echoed as ReadonlySet<string>, stop: () => { echoWatchesRef.current.delete(echoed); } };
  }, []);

  const mergeTimelineFeedIds = useCallback((
    previousIds: string[],
    incomingIds: string[],
    timelineRegistry: Record<string, InventoryEntityState<'TimelineEvent'>>,
  ): string[] => {
    const deduped = normalizeTimelineEventIdList([...incomingIds, ...previousIds]);
    const sorted = sortTimelineIdsDesc(deduped, timelineRegistry);
    return sorted.slice(0, MAX_TIMELINE_FEED_IDS);
  }, []);

  const applyTimelineRefsToIndex = useCallback((refs?: ParsedTimelineRefs) => {
    if (!refs) return;
    const k = kernel.current;
    if (!k) return;

    const hasSessionRefs = Boolean(refs.session);
    const hasSubjectRefs = Boolean(refs.subject);
    if (!hasSessionRefs && !hasSubjectRefs) return;

    const timelineRegistry = k.getState().inventory.TimelineEvent
      .registry as Record<string, InventoryEntityState<'TimelineEvent'>>;

    k.setState((prevState) => {
      const nextBySessionId = { ...prevState.timelineFeedIndex.bySessionId };
      const nextBySubjectKey = { ...prevState.timelineFeedIndex.bySubjectKey };
      const nextFeedState = { ...prevState.timelineFeedState };
      const now = Date.now();

      if (refs.session) {
        const sessionId = refs.session.sessionId;
        const previous = Array.isArray(nextBySessionId[sessionId]) ? nextBySessionId[sessionId] : [];
        nextBySessionId[sessionId] =
          refs.session.ids.length === 0 && refs.session.hasMoreOlder === false
            ? []
            : mergeTimelineFeedIds(previous, refs.session.ids, timelineRegistry);
        const feedKey = buildTimelineSessionFeedKey(sessionId);
        const previousFeedState = nextFeedState[feedKey];
        nextFeedState[feedKey] = {
          hasMoreOlder: refs.session.hasMoreOlder,
          olderCursor: refs.session.olderCursor,
          isLoading: previousFeedState?.isLoading === true ? true : false,
          updatedAt: now,
        };
      }

      if (refs.subject) {
        const subjectKey = refs.subject.subjectKey;
        const previous = Array.isArray(nextBySubjectKey[subjectKey]) ? nextBySubjectKey[subjectKey] : [];
        nextBySubjectKey[subjectKey] =
          refs.subject.ids.length === 0 && refs.subject.hasMoreOlder === false
            ? []
            : mergeTimelineFeedIds(previous, refs.subject.ids, timelineRegistry);
        const feedKey = buildTimelineSubjectFeedKey(subjectKey);
        const previousFeedState = nextFeedState[feedKey];
        nextFeedState[feedKey] = {
          hasMoreOlder: refs.subject.hasMoreOlder,
          olderCursor: refs.subject.olderCursor,
          isLoading: previousFeedState?.isLoading === true ? true : false,
          updatedAt: now,
        };
      }

      return {
        ...prevState,
        timelineFeedIndex: {
          bySessionId: nextBySessionId,
          bySubjectKey: nextBySubjectKey,
        },
        timelineFeedState: nextFeedState,
      };
    });
  }, [kernel, mergeTimelineFeedIds]);

  type TimelineIndexMutation =
    | { type: 'upsert'; eventId: string; sessionId?: string; subjectKey?: string }
    | { type: 'remove'; eventId: string; sessionId?: string; subjectKey?: string }
    | { type: 'purge'; eventId: string };

  const applyTimelineIndexMutations = useCallback((mutations: TimelineIndexMutation[]) => {
    if (!mutations.length) return;
    const k = kernel.current;
    if (!k) return;

    const timelineRegistry = k.getState().inventory.TimelineEvent
      .registry as Record<string, InventoryEntityState<'TimelineEvent'>>;

    k.setState((prevState) => {
      const nextBySessionId = { ...prevState.timelineFeedIndex.bySessionId };
      const nextBySubjectKey = { ...prevState.timelineFeedIndex.bySubjectKey };
      const touchedSessionIds = new Set<string>();
      const touchedSubjectKeys = new Set<string>();

      const removeFromSession = (sessionId: string, eventId: string) => {
        const current = nextBySessionId[sessionId];
        if (!Array.isArray(current) || current.length === 0) return;
        const next = current.filter((id) => id !== eventId);
        if (next.length === current.length) return;
        nextBySessionId[sessionId] = next;
        touchedSessionIds.add(sessionId);
      };

      const removeFromSubject = (subjectKey: string, eventId: string) => {
        const current = nextBySubjectKey[subjectKey];
        if (!Array.isArray(current) || current.length === 0) return;
        const next = current.filter((id) => id !== eventId);
        if (next.length === current.length) return;
        nextBySubjectKey[subjectKey] = next;
        touchedSubjectKeys.add(subjectKey);
      };

      const upsertInSession = (sessionId: string, eventId: string) => {
        const current = Array.isArray(nextBySessionId[sessionId]) ? nextBySessionId[sessionId] : [];
        if (!current.includes(eventId)) {
          nextBySessionId[sessionId] = [eventId, ...current];
        }
        touchedSessionIds.add(sessionId);
      };

      const upsertInSubject = (subjectKey: string, eventId: string) => {
        const current = Array.isArray(nextBySubjectKey[subjectKey]) ? nextBySubjectKey[subjectKey] : [];
        if (!current.includes(eventId)) {
          nextBySubjectKey[subjectKey] = [eventId, ...current];
        }
        touchedSubjectKeys.add(subjectKey);
      };

      const purgeEverywhere = (eventId: string) => {
        Object.keys(nextBySessionId).forEach((sessionId) => removeFromSession(sessionId, eventId));
        Object.keys(nextBySubjectKey).forEach((subjectKey) => removeFromSubject(subjectKey, eventId));
      };

      mutations.forEach((mutation) => {
        if (!mutation.eventId) return;
        if (mutation.type === 'purge') {
          purgeEverywhere(mutation.eventId);
          return;
        }
        if (mutation.type === 'remove') {
          if (mutation.sessionId) removeFromSession(mutation.sessionId, mutation.eventId);
          if (mutation.subjectKey) removeFromSubject(mutation.subjectKey, mutation.eventId);
          return;
        }
        if (mutation.sessionId) upsertInSession(mutation.sessionId, mutation.eventId);
        if (mutation.subjectKey) upsertInSubject(mutation.subjectKey, mutation.eventId);
      });

      touchedSessionIds.forEach((sessionId) => {
        const current = nextBySessionId[sessionId] ?? [];
        nextBySessionId[sessionId] = sortTimelineIdsDesc(
          normalizeTimelineEventIdList(current),
          timelineRegistry,
        ).slice(0, MAX_TIMELINE_FEED_IDS);
      });

      touchedSubjectKeys.forEach((subjectKey) => {
        const current = nextBySubjectKey[subjectKey] ?? [];
        nextBySubjectKey[subjectKey] = sortTimelineIdsDesc(
          normalizeTimelineEventIdList(current),
          timelineRegistry,
        ).slice(0, MAX_TIMELINE_FEED_IDS);
      });

      return {
        ...prevState,
        timelineFeedIndex: {
          bySessionId: nextBySessionId,
          bySubjectKey: nextBySubjectKey,
        },
      };
    });
  }, [kernel]);

  const readEntityDetailCoverage = useCallback((
    entityType: KernelEntityType,
    entityId: string
  ): InventoryDetailCoverage => {
    const normalizedEntityId = typeof entityId === 'string' ? entityId.trim() : '';
    if (!kernel.current || !entityType || !normalizedEntityId) return null;
    const runtime = kernel.current.readEntityRuntimeState(entityType, normalizedEntityId);
    return runtime.inventoryDetailCoverage === DETAIL_COVERAGE_FULL
      ? DETAIL_COVERAGE_FULL
      : null;
  }, [kernel]);

  const setEntityDetailCoverage = useCallback((
    entityType: KernelEntityType,
    entityId: string,
    coverage: InventoryDetailCoverage
  ) => {
    const normalizedEntityId = typeof entityId === 'string' ? entityId.trim() : '';
    if (!kernel.current || !entityType || !normalizedEntityId) return;
    kernel.current.patchEntityRuntimeState(entityType, normalizedEntityId, {
      inventoryDetailCoverage: coverage ?? undefined,
    });
  }, [kernel]);

  const readAutosaveState = useCallback((
    entityType: KernelEntityType,
    entityId: string,
    scopeInput?: string
  ): AutosaveState => {
    const k = kernel.current;
    const normalizedEntityId = entityId.trim();
    if (!k || !entityType || normalizedEntityId.length === 0) {
      return createDefaultAutosaveState();
    }
    const scope = normalizePersistScope(scopeInput);
    const draft = k.readEntityKernelState(entityType, normalizedEntityId);
    if (!isRecord(draft) || !isRecord(draft.runtime)) {
      return createDefaultAutosaveState();
    }
    const runtime = draft.runtime as Record<string, unknown>;
    const inventoryPersist = runtime.inventoryPersist;
    if (!isRecord(inventoryPersist)) {
      return createDefaultAutosaveState();
    }
    const scoped = inventoryPersist[scope];
    if (!isRecord(scoped)) {
      return createDefaultAutosaveState();
    }
    return {
      dirty: scoped.dirty === true,
      saveState:
        scoped.saveState === 'queued' ||
        scoped.saveState === 'saving' ||
        scoped.saveState === 'saved' ||
        scoped.saveState === 'error'
          ? scoped.saveState
          : 'idle',
      lastDraftSignature:
        typeof scoped.lastDraftSignature === 'string' ? scoped.lastDraftSignature : null,
      lastPersistedSignature:
        typeof scoped.lastPersistedSignature === 'string' ? scoped.lastPersistedSignature : null,
      retryCount:
        typeof scoped.retryCount === 'number' && Number.isFinite(scoped.retryCount)
          ? scoped.retryCount
          : 0,
      lastError: typeof scoped.lastError === 'string' ? scoped.lastError : null,
      updatedAt:
        typeof scoped.updatedAt === 'number' && Number.isFinite(scoped.updatedAt)
          ? scoped.updatedAt
          : 0,
    };
  }, [kernel]);

  const getUnobservedAutosaveValues = useCallback((
    entityType: KernelEntityType,
    entityId: string,
  ): Array<Record<string, unknown>> => {
    const normalizedEntityId = entityId.trim();
    const recordsKey = buildSentRecordsKey(entityType, normalizedEntityId);
    const records = sentAutosaveRecordsRef.current.get(recordsKey);
    if (!records) return [];
    const unobserved = releaseObservedSentValues(
      records,
      readCanonicalEntity(kernel.current, entityType, normalizedEntityId),
      Date.now()
    );
    if (unobserved.length > 0) sentAutosaveRecordsRef.current.set(recordsKey, unobserved);
    else sentAutosaveRecordsRef.current.delete(recordsKey);
    return unobserved.map((record) => record.values);
  }, [kernel]);

  const markStudioWorkspaceFieldsSent = useCallback<useInventoryReturnType['markStudioWorkspaceFieldsSent']>((workspaceId, fields) => {
    const sentKey = buildSentRecordsKey('StudioWorkspace', workspaceId);
    lastSentFieldValuesRef.current.set(sentKey, { ...lastSentFieldValuesRef.current.get(sentKey), ...fields });
  }, []);

  // Through the handle: queueAutosave is declared below and flushes back into releaseSentAutosaveRecord.
  const queueStudioWorkspaceSave = useCallback<useInventoryReturnType['queueStudioWorkspaceSave']>((workspaceId, projectId, changes) => {
    kernel.current?.queueAutosave<Partial<StudioWorkspaceAutosavePayload>>({
      entityType: 'StudioWorkspace',
      entityId: workspaceId,
      scope: STUDIO_WORKSPACE_DTO_AUTOSAVE_SCOPE,
      reason: 'StudioWorkspace DTO autosave',
      debounceMs: STUDIO_WORKSPACE_DTO_AUTOSAVE_DEBOUNCE_MS,
      maxRetries: 0,
      buildPayload: () => changes,
      mergePending: (pending, next) => ({ ...pending, ...next }),
      fieldDeltas: true,
      persist: async (payload) => {
        const k = kernel.current;
        if (!k) return false;
        markStudioWorkspaceFieldsSent(workspaceId, payload);
        try {
          await k.queueStudioWrite([{
            action: 'UPDATE',
            entity: 'StudioWorkspace',
            id: workspaceId,
            payload: payload as Record<string, unknown>,
          } as any], { batch: true });
          return true;
        } catch (error) {
          const rollbackValue = resolveWorkspaceAnnotationOpaqueRollback(
            kernel.current?.getState().inventory.StudioWorkspace.registry[workspaceId],
            payload.annotationOpaque !== undefined && kernel.current?.getWorkspace(workspaceId)?.annotationOpaque === payload.annotationOpaque,
          );
          // Written as a change, not a bare draft revert: the failed delta is folded under the next
          // write, and would otherwise send the reverted value again.
          if (rollbackValue !== null) {
            // setWorkspace refuses only a workspace that left its project since: nothing is left to roll
            // back, and the save's own error is the one to report.
            try {
              kernel.current?.setWorkspace(workspaceId, live => ({ ...live, annotationOpaque: rollbackValue }), projectId);
            } catch { /* see above */ }
          }
          throw error;
        }
      },
    });
  }, [kernel, markStudioWorkspaceFieldsSent]);

  // A workspace save carries only the fields its own write changed, so a field written while the row
  // was absent (its CREATE unanswered) or kept by a reconciliation is sent by no later save: this
  // sends it. Both sides go through the write path's own projection, the row's bigint fields being
  // strings the draft holds as numbers. A workspace deleted locally has no residual: its draft is the
  // delete marker alone.
  const queueStudioWorkspaceDraftResidual = useCallback((entityType: KernelEntityType, entityId: string) => {
    const k = kernel.current;
    if (!k || entityType !== 'StudioWorkspace') return;
    const row = k.getState().inventory.StudioWorkspace.registry[entityId];
    const shown = k.readEntityKernelMergedState(entityType, entityId);
    // A row detached from its project (a deleted workspace synced back) has nothing to save.
    if (!row?.projectId || !shown) return;
    const toSavable = (workspace: StudioWorkspace): Record<string, unknown> =>
      toStudioWorkspaceAutosavePayload(toStudioWorkspaceDtoFromRuntime(toWorkspaceState(workspace), row, row.projectId, row.updatedAt));
    const saved = toSavable(row);
    const unsent = autosaveQueueRef.current.get(buildPersistEntryKey(entityType, entityId, STUDIO_WORKSPACE_DTO_AUTOSAVE_SCOPE))?.pendingPayload;
    const lastSent = lastSentFieldValuesRef.current.get(buildSentRecordsKey(entityType, entityId));
    const residual = Object.fromEntries(Object.entries(toSavable(shown as unknown as StudioWorkspace)).filter(([key, value]) =>
      !deepEqual(value, saved[key])
      && !(isRecord(unsent) && hasOwn(unsent, key))
      && !(lastSent && hasOwn(lastSent, key) && deepEqual(value, lastSent[key]))
    ));
    if (Object.keys(residual).length > 0) queueStudioWorkspaceSave(entityId, row.projectId, residual);
  }, [kernel, queueStudioWorkspaceSave]);

  // Released outside an echo, nothing else re-prunes the draft this send was protecting.
  const releaseSentAutosaveRecord = useCallback((
    entityType: KernelEntityType,
    entityId: string,
    sentRecord: SentAutosaveRecord,
  ) => {
    const recordsKey = buildSentRecordsKey(entityType, entityId);
    const remainingRecords = (sentAutosaveRecordsRef.current.get(recordsKey) ?? [])
      .filter((record) => record !== sentRecord);
    if (remainingRecords.length > 0) sentAutosaveRecordsRef.current.set(recordsKey, remainingRecords);
    else sentAutosaveRecordsRef.current.delete(recordsKey);
    kernel.current?.reconcileEntityKernelState(entityType, entityId);
    queueStudioWorkspaceDraftResidual(entityType, entityId);
  }, [kernel, queueStudioWorkspaceDraftResidual]);

  const patchAutosaveState = useCallback((
    entityType: KernelEntityType,
    entityId: string,
    scopeInput: string | undefined,
    patch: Partial<AutosaveState>,
  ) => {
    const k = kernel.current;
    const normalizedEntityId = entityId.trim();
    if (!k || !entityType || normalizedEntityId.length === 0) return;

    const scope = normalizePersistScope(scopeInput);
    const draft = k.readEntityKernelState(entityType, normalizedEntityId);
    // Written into an entity with neither draft nor row, this bookkeeping alone would show as one.
    if (!isRecord(draft) && !readCanonicalEntity(k, entityType, normalizedEntityId)) return;
    const currentRuntime = isRecord(draft) && isRecord(draft.runtime)
      ? (draft.runtime as Record<string, unknown>)
      : {};
    const currentInventoryPersist = isRecord(currentRuntime.inventoryPersist)
      ? (currentRuntime.inventoryPersist as Record<string, unknown>)
      : {};
    const currentScope = isRecord(currentInventoryPersist[scope])
      ? (currentInventoryPersist[scope] as Record<string, unknown>)
      : {};

    const normalizedPatchEntries = Object.entries(patch).filter(([, value]) => value !== undefined);
    if (normalizedPatchEntries.length === 0) {
      return;
    }

    const hasRealChange = normalizedPatchEntries.some(([key, value]) => !Object.is(currentScope[key], value));
    if (!hasRealChange) {
      return;
    }

    const nextScoped = Object.fromEntries(normalizedPatchEntries) as Record<string, unknown>;
    nextScoped.updatedAt = Date.now();

    k.mergeEntityKernelState(entityType, normalizedEntityId, {
      runtime: {
        inventoryPersist: {
          [scope]: nextScoped,
        },
      },
    });
  }, [kernel]);

  const clearPersistTimer = useCallback((entryKey: string) => {
    const entry = autosaveQueueRef.current.get(entryKey);
    if (!entry) return;
    if (entry.timer) {
      clearTimeout(entry.timer);
      entry.timer = null;
    }
  }, []);

  const cancelAutosave = useCallback((
    entityType: KernelEntityType,
    entityId: string,
    scopeInput?: string
  ) => {
    const normalizedEntityId = entityId.trim();
    if (!entityType || normalizedEntityId.length === 0) return;
    const scope = normalizePersistScope(scopeInput);
    const entryKey = buildPersistEntryKey(entityType, normalizedEntityId, scope);
    clearPersistTimer(entryKey);
    const entry = autosaveQueueRef.current.get(entryKey);
    if (entry && !entry.inFlight) {
      autosaveQueueRef.current.delete(entryKey);
      patchAutosaveState(entityType, normalizedEntityId, scope, {
        saveState: 'idle',
      });
    }
  }, [clearPersistTimer, patchAutosaveState]);

  const settleAutosave = useCallback((
    entityType: KernelEntityType,
    entityId: string,
    options?: {
      scope?: string;
      signature?: string;
      saveState?: Extract<AutosaveStatus, 'idle' | 'saved'>;
    }
  ) => {
    const normalizedEntityId = entityId.trim();
    if (!entityType || normalizedEntityId.length === 0) return;
    const scope = normalizePersistScope(options?.scope);
    const entryKey = buildPersistEntryKey(entityType, normalizedEntityId, scope);

    clearPersistTimer(entryKey);
    const entry = autosaveQueueRef.current.get(entryKey);
    if (entry && !entry.inFlight) {
      autosaveQueueRef.current.delete(entryKey);
    }

    const currentPersist = readAutosaveState(entityType, normalizedEntityId, scope);
    const normalizedSignature = typeof options?.signature === 'string'
      ? options.signature
      : null;
    const signature =
      normalizedSignature ??
      currentPersist.lastDraftSignature ??
      currentPersist.lastPersistedSignature;

    patchAutosaveState(entityType, normalizedEntityId, scope, {
      dirty: false,
      saveState: options?.saveState ?? 'idle',
      lastDraftSignature: signature ?? null,
      lastPersistedSignature: signature ?? currentPersist.lastPersistedSignature,
      retryCount: 0,
      lastError: null,
    });
  }, [clearPersistTimer, patchAutosaveState, readAutosaveState]);

  const flushAutosave = useCallback(async (
    entityType: KernelEntityType,
    entityId: string,
    scopeInput?: string
  ): Promise<boolean> => {
    const normalizedEntityId = entityId.trim();
    if (!entityType || normalizedEntityId.length === 0) return false;
    const scope = normalizePersistScope(scopeInput);
    const entryKey = buildPersistEntryKey(entityType, normalizedEntityId, scope);
    const entry = autosaveQueueRef.current.get(entryKey);
    if (!entry || entry.inFlight) return false;
    const cleanupEntryIfIdle = () => {
      const current = autosaveQueueRef.current.get(entryKey);
      if (!current) return;
      if (current.inFlight) return;
      if (current.timer) return;
      if (current.pendingPayload !== null && current.pendingPayload !== undefined) return;
      if (typeof current.pendingSignature === 'string' && current.pendingSignature.length > 0) return;
      autosaveQueueRef.current.delete(entryKey);
    };

    clearPersistTimer(entryKey);

    const payload = entry.pendingPayload ?? entry.buildPayload();
    if (payload === null || payload === undefined) {
      patchAutosaveState(entityType, normalizedEntityId, scope, {
        dirty: false,
        saveState: 'idle',
      });
      cleanupEntryIfIdle();
      return false;
    }

    const signature = entry.pendingSignature ?? entry.serializePayload(payload);
    const currentPersist = readAutosaveState(entityType, normalizedEntityId, scope);
    if (
      !entry.fieldDeltas &&
      currentPersist.lastPersistedSignature &&
      currentPersist.lastPersistedSignature === signature &&
      currentPersist.dirty === false
    ) {
      patchAutosaveState(entityType, normalizedEntityId, scope, {
        saveState: currentPersist.saveState === 'error' ? 'idle' : currentPersist.saveState,
        lastDraftSignature: signature,
        lastError: null,
        retryCount: 0,
      });
      entry.pendingPayload = null;
      entry.pendingSignature = null;
      cleanupEntryIfIdle();
      return true;
    }

    entry.inFlight = true;
    let land!: () => void;
    entry.landed = new Promise<void>((resolve) => { land = resolve; });
    entry.pendingPayload = null;
    entry.pendingSignature = null;
    const sentRecord = toSentAutosaveRecord(payload, readCanonicalEntity(kernel.current, entityType, normalizedEntityId));
    if (sentRecord) {
      const sentRecordsKey = buildSentRecordsKey(entityType, normalizedEntityId);
      sentAutosaveRecordsRef.current.set(sentRecordsKey, [...(sentAutosaveRecordsRef.current.get(sentRecordsKey) ?? []), sentRecord]);
    }

    patchAutosaveState(entityType, normalizedEntityId, scope, {
      dirty: true,
      saveState: 'saving',
      lastDraftSignature: signature,
      lastError: null,
    });

    let attempt = 0;
    let lastError: unknown = null;
    while (attempt <= entry.maxRetries) {
      const context = {
        entityType,
        entityId: normalizedEntityId,
        scope,
        reason: entry.reason,
        signature,
        attempt,
      };

      try {
        const result = await entry.persist(payload, context);
        if (result === false) {
          throw new Error('Persist callback returned false.');
        }
        if (sentRecord && isRecord(result) && isRecord(result.sent)) {
          sentRecord.values = toSentAutosaveRecord(result.sent, readCanonicalEntity(kernel.current, entityType, normalizedEntityId))?.values ?? {};
        }

        const livePendingSignature =
          autosaveQueueRef.current.get(entryKey)?.pendingSignature ?? null;
        const queuedNextSignature =
          typeof livePendingSignature === 'string' &&
          livePendingSignature.length > 0 &&
          livePendingSignature !== signature
            ? livePendingSignature
            : null;
        const hasQueuedNextPayload = queuedNextSignature !== null;
        patchAutosaveState(entityType, normalizedEntityId, scope, {
          dirty: hasQueuedNextPayload,
          saveState: hasQueuedNextPayload ? 'queued' : 'saved',
          lastDraftSignature: hasQueuedNextPayload ? queuedNextSignature : signature,
          lastPersistedSignature: signature,
          retryCount: 0,
          lastError: null,
        });
        entry.inFlight = false;
        land();
        if (sentRecord) {
          sentRecord.settledAt = Date.now();
          setTimeout(() => {
            releaseSentAutosaveRecord(entityType, normalizedEntityId, sentRecord);
          }, SENT_AUTOSAVE_OBSERVATION_TIMEOUT_MS);
        }
        if (hasQueuedNextPayload) {
          entry.timer = setTimeout(() => {
            void flushAutosave(entityType, normalizedEntityId, scope);
          }, 0);
        } else {
          cleanupEntryIfIdle();
        }
        return true;
      } catch (error) {
        lastError = error;
        patchAutosaveState(entityType, normalizedEntityId, scope, {
          dirty: true,
          saveState: 'error',
          retryCount: attempt + 1,
          lastError: error instanceof Error ? error.message : 'Persist failed',
        });

        const hasNewPayloadQueued =
          typeof entry.pendingSignature === 'string' &&
          entry.pendingSignature !== signature;
        if (hasNewPayloadQueued) {
          break;
        }
        if (attempt >= entry.maxRetries) {
          break;
        }
        const retryDelay = entry.retryDelayMs * Math.max(1, attempt + 1);
        await new Promise<void>((resolve) => {
          setTimeout(resolve, retryDelay);
        });
      }
      attempt += 1;
    }

    entry.inFlight = false;
    land();
    if (sentRecord) releaseSentAutosaveRecord(entityType, normalizedEntityId, sentRecord);
    // A later field delta does not repeat a failed one's changes: the failed payload is folded under the next write instead of dropped.
    // One the queue set aside is dropped: sent again as is, it would fail again and take every later save of the entity with it.
    if (entry.fieldDeltas && entry.mergePending && !isSetAsideStudioWrite(lastError)) {
      entry.pendingPayload = entry.pendingPayload === null || entry.pendingPayload === undefined
        ? payload
        : entry.mergePending(payload, entry.pendingPayload);
      entry.pendingSignature = entry.serializePayload(entry.pendingPayload);
    }
    if (entry.pendingSignature && entry.pendingSignature !== signature) {
      entry.timer = setTimeout(() => {
        void flushAutosave(entityType, normalizedEntityId, scope);
      }, 0);
      return false;
    }

    if (lastError) {
      patchAutosaveState(entityType, normalizedEntityId, scope, {
        dirty: true,
        saveState: 'error',
        lastError: lastError instanceof Error ? lastError.message : 'Persist failed',
      });
    }
    cleanupEntryIfIdle();
    return false;
  }, [
    clearPersistTimer,
    kernel,
    patchAutosaveState,
    readAutosaveState,
    releaseSentAutosaveRecord,
  ]);

  const awaitAutosaveInFlight = useCallback<useInventoryReturnType['awaitAutosaveInFlight']>((entityType, entityId, scope) =>
    autosaveQueueRef.current.get(buildPersistEntryKey(entityType, entityId.trim(), normalizePersistScope(scope)))?.landed ?? Promise.resolve(), []);

  const prepareStudioWrite = useCallback(<T,>(preparation: Promise<T>): Promise<T> => {
    studioWritesPreparingRef.current += 1;
    return preparation.finally(() => { studioWritesPreparingRef.current -= 1; });
  }, []);

  // Hands every unsent Studio payload of the engine to the write queue now, without its debounce, in
  // the order each entry was first written to. Synchronous: flushAutosave and each Studio persist
  // reach queueStudioWrite before their first await, or count as preparing. An entry takes one payload
  // at a time: a payload written while the earlier one is being saved stays with the engine, which
  // sends it as soon as that one settles. True when one stayed, or is still preparing.
  const handOverStudioAutosaves = useCallback((): boolean => {
    let stayed = false;
    autosaveQueueRef.current.forEach((entry) => {
      if (!isUnsentStudioAutosave(entry)) return;
      if (entry.inFlight) stayed = true;
      else void flushAutosave(entry.entityType, entry.entityId, entry.scope);
    });
    return stayed || studioWritesPreparingRef.current > 0;
  }, [flushAutosave]);

  const queueAutosave = useCallback(<TPayload = unknown>(options: QueueAutosaveOptions<TPayload>) => {
    const k = kernel.current;
    if (!k) return;

    const entityType = options.entityType;
    const normalizedEntityId = options.entityId.trim();
    if (!entityType || normalizedEntityId.length === 0) return;

    const scope = normalizePersistScope(options.scope);
    const entryKey = buildPersistEntryKey(entityType, normalizedEntityId, scope);
    const debounceMs = Math.max(0, Math.floor(options.debounceMs ?? DEFAULT_ENTITY_PERSIST_DEBOUNCE_MS));
    const maxRetries = Math.max(0, Math.floor(options.maxRetries ?? DEFAULT_ENTITY_PERSIST_MAX_RETRIES));
    const retryDelayMs = Math.max(100, Math.floor(options.retryDelayMs ?? DEFAULT_ENTITY_PERSIST_RETRY_DELAY_MS));
    const serializePayload = options.serializePayload
      ? ((payload: unknown) => options.serializePayload!(payload as TPayload))
      : ((payload: unknown) => stableStringify(payload));

    const builtPayload = options.buildPayload();
    if (builtPayload === null || builtPayload === undefined) {
      cancelAutosave(entityType, normalizedEntityId, scope);
      return;
    }
    // A delta payload replacing an unsent one would drop the earlier change (two writes inside
    // one debounce window). The pending one is cleared the moment a flush starts, so only the
    // unsent delta is ever folded in.
    const unsentPayload = autosaveQueueRef.current.get(buildPersistEntryKey(entityType, normalizedEntityId, scope))?.pendingPayload;
    const payload = options.mergePending && unsentPayload !== null && unsentPayload !== undefined
      ? options.mergePending(unsentPayload as TPayload, builtPayload as TPayload)
      : builtPayload;
    const signature = serializePayload(payload);
    const currentPersist = readAutosaveState(entityType, normalizedEntityId, scope);

    if (
      !options.fieldDeltas &&
      currentPersist.lastPersistedSignature &&
      currentPersist.lastPersistedSignature === signature &&
      currentPersist.dirty === false
    ) {
      patchAutosaveState(entityType, normalizedEntityId, scope, {
        dirty: false,
        saveState: currentPersist.saveState === 'error' ? 'idle' : currentPersist.saveState,
        lastDraftSignature: signature,
        lastError: null,
        retryCount: 0,
      });
      return;
    }

    const existing = autosaveQueueRef.current.get(entryKey);
    const nextEntry: AutosaveQueueEntry = existing ?? {
      entityType,
      entityId: normalizedEntityId,
      scope,
      reason: options.reason,
      debounceMs,
      maxRetries,
      retryDelayMs,
      buildPayload: options.buildPayload as () => unknown,
      serializePayload,
      persist: options.persist as QueueAutosaveOptions<unknown>['persist'],
      mergePending: options.mergePending as QueueAutosaveOptions<unknown>['mergePending'],
      fieldDeltas: options.fieldDeltas === true,
      timer: null,
      inFlight: false,
      landed: Promise.resolve(),
      pendingPayload: null,
      pendingSignature: null,
    };

    // Important: keep the same object reference while a flush is in-flight.
    // Replacing the entry object breaks flush's in-flight bookkeeping.
    nextEntry.reason = options.reason;
    nextEntry.debounceMs = debounceMs;
    nextEntry.maxRetries = maxRetries;
    nextEntry.retryDelayMs = retryDelayMs;
    nextEntry.buildPayload = options.mergePending ? () => payload : options.buildPayload as () => unknown;
    nextEntry.serializePayload = serializePayload;
    nextEntry.persist = options.persist as QueueAutosaveOptions<unknown>['persist'];
    nextEntry.mergePending = options.mergePending as QueueAutosaveOptions<unknown>['mergePending'];
    nextEntry.fieldDeltas = options.fieldDeltas === true;

    nextEntry.pendingPayload = payload;
    nextEntry.pendingSignature = signature;
    autosaveQueueRef.current.set(entryKey, nextEntry);

    patchAutosaveState(entityType, normalizedEntityId, scope, {
      dirty: true,
      saveState: nextEntry.inFlight ? 'saving' : 'queued',
      lastDraftSignature: signature,
      lastError: null,
    });

    if (nextEntry.timer) {
      clearTimeout(nextEntry.timer);
    }
    nextEntry.timer = setTimeout(() => {
      const latest = autosaveQueueRef.current.get(entryKey);
      if (!latest) return;

      if (latest.inFlight) {
        // Safety net: if a save is in-flight, keep a lightweight retry timer so
        // newly queued payloads cannot get stuck waiting forever.
        latest.timer = setTimeout(() => {
          void flushAutosave(entityType, normalizedEntityId, scope);
        }, Math.max(150, debounceMs));
        return;
      }

      void flushAutosave(entityType, normalizedEntityId, scope);
    }, debounceMs);
  }, [
    cancelAutosave,
    flushAutosave,
    kernel,
    patchAutosaveState,
    readAutosaveState,
  ]);

  const setAutosaveBaseline = useCallback((
    entityType: KernelEntityType,
    entityId: string,
    signature: string,
    options?: {
      scope?: string;
      saveState?: AutosaveStatus;
    }
  ) => {
    const normalizedEntityId = entityId.trim();
    if (!entityType || normalizedEntityId.length === 0 || typeof signature !== 'string') return;
    const scope = normalizePersistScope(options?.scope);
    patchAutosaveState(entityType, normalizedEntityId, scope, {
      dirty: false,
      saveState: options?.saveState ?? 'idle',
      lastDraftSignature: signature,
      lastPersistedSignature: signature,
      retryCount: 0,
      lastError: null,
    });
  }, [patchAutosaveState]);

  const cancelAutosaveScopes = useCallback((
    entityType: KernelEntityType,
    entityId: string
  ) => {
    const normalizedEntityId = entityId.trim();
    if (!entityType || normalizedEntityId.length === 0) return;
    const prefix = `${entityType}:${normalizedEntityId}:`;
    Array.from(autosaveQueueRef.current.keys()).forEach((entryKey) => {
      if (!entryKey.startsWith(prefix)) return;
      const entry = autosaveQueueRef.current.get(entryKey);
      if (entry?.timer) {
        clearTimeout(entry.timer);
      }
      autosaveQueueRef.current.delete(entryKey);
    });
    sentAutosaveRecordsRef.current.delete(buildSentRecordsKey(entityType, normalizedEntityId));
    lastSentFieldValuesRef.current.delete(buildSentRecordsKey(entityType, normalizedEntityId));
  }, []);

  const reconcileTouchedSlice = useCallback(
    (
      entityType: InventoryEntityType | EntityType,
      ids: Array<string | null | undefined>,
      options?: { eventType?: LeasingEventType }
    ) => {
      const k = kernel.current;
      if (!k || !isKnownEntityType(entityType) || !ids.length) return;

      const entityKernelState = k.getEntityKernelState();
      const uniqueIds = Array.from(
        new Set(
          ids.filter((id): id is string => typeof id === 'string' && id.length > 0)
        )
      );

      uniqueIds.forEach((id) => {
        if (!entityKernelState[`${entityType}:${id}`]) return;
        k.reconcileEntityKernelState(entityType, id, options);
        if (options?.eventType !== 'DELETE') queueStudioWorkspaceDraftResidual(entityType, id);
      });
    },
    [kernel, queueStudioWorkspaceDraftResidual]
  );

  // A row keyed `${entity}:${id}` in skipRows is not written: an echo applied since the read left is newer.
  const hydrateInventoryData = useCallback((payload: LeasingDataResponse, skipRows?: ReadonlySet<string>) => {

    const updateSlice = <K extends InventoryEntityType>(
      key: K,
      items: unknown[] | undefined,
      extractId: (item: unknown) => string = (item) =>
        isRecord(item) && typeof item.id === 'string' ? item.id : ''
    ) => {
      if (items === undefined) return;
      const k = kernel.current;
      if (!k) return;
      // IMPORTANT: read the raw store slice here (not getInventory()).
      // getInventory() returns a merged view with entityKernelState drafts; using it as
      // a write source would leak transient UI-only fields (e.g. activeTab) into the
      // canonical inventory registry and later let reconcile prune those drafts.
      const rawSlice = k.getState().inventory[key];
      const nextRegistry = {
        ...(rawSlice?.registry ?? {}),
      } as Record<string, InventoryEntityState<K>>;
      const touchedIds: string[] = [];

      items.forEach((item) => {
        if (key === 'Notification' && (!k.getUserContext().dbUserId || !isRecord(item) || item.userId !== k.getUserContext().dbUserId)) return;
        const extractedId = extractId(item);
        const id = typeof extractedId === 'string' ? extractedId.trim() : '';
        if (!id || skipRows?.has(`${key}:${id}`)) return;

        const normalized = normalizeIncomingEntity(
          key,
          item,
          {
            entityId: id,
            existingEntity: (nextRegistry[id] as unknown as Record<string, unknown> | undefined) ?? null,
          }
        );
        if (!normalized) return;

        const normalizedId = typeof normalized.id === 'string' ? normalized.id.trim() : id;
        if (!normalizedId) return;

        // IMPORTANT: keep merge semantics to preserve omitted fields (`undefined`)
        // while allowing explicit empty arrays / null to overwrite previous values.
        const merged = {
          ...(nextRegistry[normalizedId] || {}),
          ...normalized,
        } as InventoryEntityState<K>;
        nextRegistry[normalizedId] = merged;
        touchedIds.push(normalizedId);
      });

      kernel.current?.setInventorySlice(key, (prev) => {
        return {
          ...prev,
          registry: nextRegistry,
          lastActivity: Date.now(),
          isLoading: false
        };
      });

      reconcileTouchedSlice(key, touchedIds);
    };

    updateSlice('Property', payload.properties);
    updateSlice('Floor', payload.floors);
    updateSlice('Unit', payload.units);
    updateSlice('Brand', payload.brands);
    updateSlice('Portfolio', payload.portfolios);
    updateSlice('LegalEntity', payload.legalEntities);
    updateSlice('Deal', payload.deals);
    updateSlice('MarketSignal', payload.signals);
    updateSlice('Audit', payload.audits);
    updateSlice('ChatSession', payload.chatSessions);
    updateSlice('TimelineEvent', payload.timelineEvents);
    if (Array.isArray(payload.timelineEvents) && payload.timelineEvents.length > 0) {
      const timelineMutations: TimelineIndexMutation[] = [];
      payload.timelineEvents.forEach((event) => {
        if (!isRecord(event)) return;
        const eventId = normalizeNonEmptyString(event.id);
        if (!eventId) return;
        const sessionId = normalizeNonEmptyString(event.chatSessionId);
        const targetEntityType = normalizeNonEmptyString(event.targetEntityType);
        const targetEntityId = normalizeNonEmptyString(event.targetEntityId);
        const subjectKey =
          targetEntityType && targetEntityId
            ? buildTimelineSubjectKey(targetEntityType, targetEntityId)
            : '';
        timelineMutations.push({
          type: 'upsert',
          eventId,
          ...(sessionId ? { sessionId } : {}),
          ...(subjectKey ? { subjectKey } : {}),
        });
      });
      applyTimelineIndexMutations(timelineMutations);
    }
    updateSlice('Campaign', payload.campaigns);
    updateSlice('SourcingCampaign', payload.sourcingCampaigns);
    updateSlice('PortfolioFitCampaign', payload.portfolioFitCampaigns);
    updateSlice('PortfolioFit', payload.portfolioFits);
    updateSlice('Lead', payload.leads);
    updateSlice('Project', payload.projects);
    updateSlice('Organisation', payload.organisations);
    updateSlice('CreditGrant', payload.creditGrants);
    updateSlice('SlideCorrection', payload.slideCorrections);
    updateSlice('Team', payload.teams);
    updateSlice('User', payload.users);
    updateSlice('Notification', payload.notifications);
    updateSlice('Contact', payload.contacts);
    updateSlice('UniversalFact', payload.facts);
    updateSlice('StudioWorkspace', payload.workspaces);
    updateSlice('StudioLayer', payload.layers);
    updateSlice('StudioWorkspaceVersion', payload.workspaceVersions);

    if (payload.stats) kernel.current?.setMarketSignalStats(payload.stats);
  }, [applyTimelineIndexMutations, kernel, reconcileTouchedSlice]);

  const setTimelineFeedLoading = useCallback((feedKey: string, isLoading: boolean) => {
    kernel.current?.setState((prev) => {
      const previousFeedState = prev.timelineFeedState[feedKey];
      return {
        timelineFeedState: {
          ...prev.timelineFeedState,
          [feedKey]: {
            hasMoreOlder: previousFeedState?.hasMoreOlder === true,
            olderCursor: normalizeNonEmptyString(previousFeedState?.olderCursor) || null,
            isLoading,
            updatedAt: Date.now(),
          },
        },
      };
    });
  }, [kernel]);

  const loadOlderTimelineEvents = useCallback(async (args: {
    sessionId: string;
    scope: 'session' | 'subject';
    subject?: { entityType: string; entityId: string } | null;
  }): Promise<void> => {
    const k = kernel.current;
    const endpoint = INVENTORY_ENDPOINTS.TimelineEvent;
    if (!k || !endpoint) return;

    const sessionId = normalizeNonEmptyString(args.sessionId);
    if (!sessionId) return;

    const subjectEntityType = normalizeNonEmptyString(args.subject?.entityType);
    const subjectEntityId = normalizeNonEmptyString(args.subject?.entityId);
    const subjectKey =
      args.scope === 'subject' && subjectEntityType && subjectEntityId
        ? buildTimelineSubjectKey(subjectEntityType, subjectEntityId)
        : '';
    if (args.scope === 'subject' && !subjectKey) return;

    const feedKey = subjectKey
      ? buildTimelineSubjectFeedKey(subjectKey)
      : buildTimelineSessionFeedKey(sessionId);

    const feedSnapshot = k.getState().timelineFeedState[feedKey];
    const olderCursor = normalizeNonEmptyString(feedSnapshot?.olderCursor);
    if (!olderCursor || feedSnapshot?.hasMoreOlder !== true || feedSnapshot?.isLoading === true) {
      return;
    }

    setTimelineFeedLoading(feedKey, true);
    const requestEpoch = clearEpochRef.current;

    try {
      const params = new URLSearchParams();
      params.set('limit', String(TIMELINE_FEED_LOAD_LIMIT));
      params.set('before', olderCursor);
      TIMELINE_FEED_LOAD_CATEGORIES.forEach((category) => params.append('category', category));
      if (subjectKey) {
        params.set('targetEntityType', subjectEntityType);
        params.set('targetEntityId', subjectEntityId);
      } else {
        params.set('chatSessionId', sessionId);
      }

      const response = await fetch(`${endpoint}?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Failed to load older events (${response.status})`);
      }

      const data = await response.json() as {
        timelineEvents?: unknown;
        pagination?: { hasMore?: unknown; nextCursor?: unknown } | null;
      };
      if (requestEpoch !== clearEpochRef.current) return;

      const loadedEvents = (Array.isArray(data.timelineEvents)
        ? data.timelineEvents.filter(isRecord)
        : []) as unknown as TimelineEventDTO[];
      const loadedIds = loadedEvents
        .map((event) => normalizeNonEmptyString(event.id))
        .filter((id): id is string => Boolean(id));

      const existingIds = subjectKey
        ? k.getState().timelineFeedIndex.bySubjectKey[subjectKey]
        : k.getState().timelineFeedIndex.bySessionId[sessionId];
      const existingSet = new Set(Array.isArray(existingIds) ? existingIds : []);
      const addedNew = loadedIds.some((id) => !existingSet.has(id));

      hydrateInventoryData({ timelineEvents: loadedEvents });
      applyTimelineIndexMutations(loadedIds.map((eventId) => ({
        type: 'upsert' as const,
        eventId,
        ...(subjectKey ? { subjectKey } : { sessionId }),
      })));

      const nextCursor = normalizeNonEmptyString(data.pagination?.nextCursor);
      // If this fetch produced no genuinely new events, or there is no next cursor to
      // continue from, there is nothing more to load. Force hasMoreOlder off (and clear
      // the cursor) so the "Load older events" button stops showing.
      const effectiveHasMore = data.pagination?.hasMore === true && !!nextCursor && addedNew;
      kernel.current?.setState((prev) => ({
        timelineFeedState: {
          ...prev.timelineFeedState,
          [feedKey]: {
            hasMoreOlder: effectiveHasMore,
            olderCursor: effectiveHasMore ? nextCursor : null,
            isLoading: false,
            updatedAt: Date.now(),
          },
        },
      }));
    } catch (error) {
      console.error('[useTimelineFeedMessages] Failed to load older timeline events', error);
      setTimelineFeedLoading(feedKey, false);
    }
  }, [applyTimelineIndexMutations, hydrateInventoryData, kernel, setTimelineFeedLoading]);

  const applyInventoryEvents = useCallback((incomingEvents: RealtimeEvent[]) => {
    if (!kernel.current || !incomingEvents || incomingEvents.length === 0) return;

    const events = incomingEvents.filter((e): e is RealtimeEvent => !!(e?.entityId && e?.entityType && e?.eventType));
    if (events.length === 0) return;
    const externalChangesByEntityType: InventoryExternalEntityChangesMap = new Map();
    const timelineIndexMutations: TimelineIndexMutation[] = [];

    events.forEach((event) => {
      const { entityType, entityId, eventType } = event;
      const normalizedPayload = {
        ...((event.payload && typeof event.payload === 'object' && !Array.isArray(event.payload))
          ? event.payload
          : {}),
      } as Record<string, unknown>;

      const type = entityType as InventoryEntityType;
      const slice = kernel.current?.getState().inventory[type];
      if (!slice) return;
      if (type === 'Notification' && (!kernel.current?.getUserContext().dbUserId || (normalizedPayload.userId ?? (slice.registry[entityId] as NotificationDTO | undefined)?.userId) !== kernel.current?.getUserContext().dbUserId)) return;
      if (eventType === 'DELETE') {
        cancelAutosaveScopes(entityType as KernelEntityType, entityId);
        setEntityDetailCoverage(entityType as KernelEntityType, entityId, null);
      }
      let previousEntity: InventoryEntityState<InventoryEntityType> | null = null;
      let nextEntity: InventoryEntityState<InventoryEntityType> | null = null;

      kernel.current?.setInventorySlice(type, (prev) => {
        const registry = { ...prev.registry } as Record<string, InventoryEntityState<InventoryEntityType>>;
        const { queryCache, requeryLists } = applyEventToQueryCache(prev.queryCache, {
          entityType: type,
          entityId,
          eventType: eventType as LeasingEventType,
        });
        previousEntity = (registry[entityId] as InventoryEntityState<InventoryEntityType> | undefined) ?? null;

        if (eventType === 'DELETE') {
          delete registry[entityId];
        } else if (eventType === 'CREATE') {
          const normalizedIncoming = normalizeIncomingEntity(
            type,
            { ...normalizedPayload, id: entityId },
            {
              entityId,
              existingEntity: (registry[entityId] as unknown as Record<string, unknown> | undefined) ?? null,
            }
          );
          if (normalizedIncoming) {
            registry[entityId] = normalizedIncoming;
          }
        } else {
          const hasExisting = Boolean(registry[entityId]);
          const normalizedIncoming = normalizeIncomingEntity(
            type,
            { ...normalizedPayload, id: entityId },
            {
              entityId,
              existingEntity: (registry[entityId] as unknown as Record<string, unknown> | undefined) ?? null,
              preserveMissingWorkspaceAnnotationOpaque: true,
            }
          );
          const isProjectLayerGhostUpdate =
            type === 'StudioLayer' &&
            isRecord(normalizedIncoming) &&
            normalizedIncoming.workspaceId === null;
          if (!hasExisting && (UPSERT_ON_GHOST_UPDATE_TYPES.has(type) || isProjectLayerGhostUpdate)) {
            if (normalizedIncoming) {
              registry[entityId] = normalizedIncoming;
            }
          } else if (!hasExisting) {
            // Ignore updates for entities not present in local registry.
            // We only upsert selected lightweight types (ghost updates) above.
          } else {
            if (normalizedIncoming) {
              const merged = {
                ...(registry[entityId] || {}),
                ...normalizedIncoming,
                id: entityId,
              } as InventoryEntityState<InventoryEntityType>;
              registry[entityId] = merged;
            }
          }
        }
        nextEntity = (registry[entityId] as InventoryEntityState<InventoryEntityType> | undefined) ?? null;

        return {
          ...prev,
          registry,
          queryCache,
          lastActivity: Date.now(),
          // Distinct from `lastActivity` (bumped by every event, UPDATE included): CREATE and
          // StudioLayer scope updates ask a mounted query-mode list to re-query itself.
          ...(requeryLists ? { lastCreateActivity: Date.now() } : null),
        };
      });

      echoWatchesRef.current.forEach((watch) => watch.add(`${type}:${entityId}`));
      reconcileTouchedSlice(entityType, [entityId], { eventType });
      const statsEntity = INVENTORY_ENTITY_TO_STATS_ENTITY[type];
      if (statsEntity) {
        kernel.current?.setState((prevState) => {
          const currentEntityStats = prevState.inventoryStats[statsEntity] ?? {};
          const keys = Object.keys(currentEntityStats);
          if (keys.length === 0) return prevState;

          let changed = false;
          const nextEntityStats: Record<string, InventoryStatsEntry> = { ...currentEntityStats };
          keys.forEach((key) => {
            const entry = currentEntityStats[key];
            if (!entry || entry.stale) return;
            nextEntityStats[key] = {
              ...entry,
              stale: true,
            };
            changed = true;
          });

          if (!changed) return prevState;
          return {
            ...prevState,
            inventoryStats: {
              ...prevState.inventoryStats,
              [statsEntity]: nextEntityStats,
            },
          };
        });
      }

      if (type === 'TimelineEvent') {
        const previousSessionId = extractTimelineEventChatSessionId(previousEntity);
        const nextSessionId = extractTimelineEventChatSessionId(nextEntity);
        const previousSubjectKey = extractTimelineEventSubjectKey(previousEntity);
        const nextSubjectKey = extractTimelineEventSubjectKey(nextEntity);

        if (eventType === 'DELETE') {
          if (previousSessionId || previousSubjectKey) {
            timelineIndexMutations.push({
              type: 'remove',
              eventId: entityId,
              ...(previousSessionId ? { sessionId: previousSessionId } : {}),
              ...(previousSubjectKey ? { subjectKey: previousSubjectKey } : {}),
            });
          } else {
            timelineIndexMutations.push({ type: 'purge', eventId: entityId });
          }
        } else if (eventType === 'CREATE') {
          if (nextSessionId || nextSubjectKey) {
            timelineIndexMutations.push({
              type: 'upsert',
              eventId: entityId,
              ...(nextSessionId ? { sessionId: nextSessionId } : {}),
              ...(nextSubjectKey ? { subjectKey: nextSubjectKey } : {}),
            });
          }
        } else {
          if (
            (previousSessionId && previousSessionId !== nextSessionId) ||
            (previousSubjectKey && previousSubjectKey !== nextSubjectKey)
          ) {
            timelineIndexMutations.push({
              type: 'remove',
              eventId: entityId,
              ...(previousSessionId ? { sessionId: previousSessionId } : {}),
              ...(previousSubjectKey ? { subjectKey: previousSubjectKey } : {}),
            });
          }
          if (nextSessionId || nextSubjectKey) {
            timelineIndexMutations.push({
              type: 'upsert',
              eventId: entityId,
              ...(nextSessionId ? { sessionId: nextSessionId } : {}),
              ...(nextSubjectKey ? { subjectKey: nextSubjectKey } : {}),
            });
          }
        }
      }

      if (hasInventoryExternalEntityExtension(type)) {
        const bucket = externalChangesByEntityType.get(type) ?? [];
        bucket.push({
          entityType: type,
          entityId,
          eventType,
          previousEntity,
          nextEntity,
          event,
        });
        externalChangesByEntityType.set(type, bucket);
      }
    });
    if (externalChangesByEntityType.size > 0) {
      dispatchInventoryExternalEntityExtensions({
        kernel,
        source: 'socket',
        changesByEntityType: externalChangesByEntityType,
      });
    }
    if (timelineIndexMutations.length > 0) {
      applyTimelineIndexMutations(timelineIndexMutations);
    }
  }, [applyTimelineIndexMutations, cancelAutosaveScopes, kernel, reconcileTouchedSlice, setEntityDetailCoverage]);

  const syncEntity = useCallback(async <K extends InventoryEntityType>(
    type: K,
    id: string,
    options: { force?: boolean; requireFresh?: boolean } = {}
  ): Promise<InventoryEntityState<K> | null> => {
    if (!kernel.current || !id?.trim()) return null;

    const forceRootFetch = options.force === true || options.requireFresh === true;
    const visited = new Set<string>();
    const syncRecursive = async <T extends InventoryEntityType>(
      recursiveType: T,
      recursiveId: string,
      forceFetch = false
    ): Promise<InventoryEntityState<T> | null> => {
      if (!kernel.current || !recursiveId?.trim()) return null;
      const trimmedId = recursiveId.trim();
      const visitKey = `${recursiveType}:${trimmedId}`;
      const endpoint = INVENTORY_ENDPOINTS[recursiveType];
      if (!endpoint) return null;

      const slice = kernel.current.getState().inventory[recursiveType];
      const cached = slice.registry[trimmedId];

      if (visited.has(visitKey)) {
        return (cached as InventoryEntityState<T>) || null;
      }
      visited.add(visitKey);

      if (!forceFetch && cached && readEntityDetailCoverage(recursiveType as KernelEntityType, trimmedId) === DETAIL_COVERAGE_FULL) {
        return cached as InventoryEntityState<T>;
      }

      const inflight = inflightDetailFetchesRef.current.get(visitKey);
      if (!forceFetch && inflight) {
        return inflight as Promise<InventoryEntityState<T> | null>;
      }
      if (forceFetch && inflight) {
        inflightDetailAbortRef.current.get(visitKey)?.abort();
      }

      const requestEpoch = clearEpochRef.current;
      const abortController = new AbortController();
      inflightDetailAbortRef.current.set(visitKey, abortController);
      const request = (async () => {
        try {
          const response = await fetch(`${endpoint}/${trimmedId}`, {
            method: 'GET',
            cache: 'no-store',
            signal: abortController.signal,
          });
          if (requestEpoch !== clearEpochRef.current) {
            if (options.requireFresh && forceFetch) throw new Error('Fresh detail sync was cancelled.');
            return null;
          }
          if (!response.ok) {
            if (response.status === 404) {
              if (requestEpoch !== clearEpochRef.current) {
                return null;
              }
              cancelAutosaveScopes(recursiveType as KernelEntityType, trimmedId);
              setEntityDetailCoverage(recursiveType as KernelEntityType, trimmedId, null);
              kernel.current?.setInventorySlice(recursiveType, (prev) => {
                const registry = { ...prev.registry };
                delete registry[trimmedId];

                // A 404 on the detail endpoint is a deletion the client missed.
                const { queryCache } = applyEventToQueryCache(prev.queryCache, {
                  entityType: recursiveType,
                  entityId: trimmedId,
                  eventType: 'DELETE',
                });

                return {
                  ...prev,
                  registry,
                  queryCache,
                  lastActivity: Date.now(),
                };
              });
              reconcileTouchedSlice(recursiveType, [trimmedId], { eventType: 'DELETE' });
              return null;
            }
            if ((response.status === 405 || response.status === 501) && !(options.requireFresh && forceFetch)) {
              console.warn(`[Inventory] Detail endpoint not supported for ${recursiveType} (HTTP ${response.status}).`);
              return (cached as InventoryEntityState<T>) || null;
            }
            throw new Error(`Fetch failed for ${recursiveType}/${trimmedId}: ${response.status}`);
          }
          const rawPayload = await response.json();
          if (requestEpoch !== clearEpochRef.current) {
            if (options.requireFresh && forceFetch) throw new Error('Fresh detail sync was cancelled.');
            return null;
          }

          const parsed = parseDetailResponsePayload(rawPayload);
          if (options.requireFresh && forceFetch && (!parsed.entity || typeof parsed.entity !== 'object' || !('id' in parsed.entity) || parsed.entity.id !== trimmedId)) {
            throw new Error(`Invalid fresh detail response for ${recursiveType}/${trimmedId}`);
          }
          const timelineRefs = parsed.timelineRefs;
          const timelineRefIds = normalizeTimelineEventIdList([
            ...(timelineRefs?.session?.ids ?? []),
            ...(timelineRefs?.subject?.ids ?? []),
          ]);

          let fetchedEntity: InventoryEntityState<T> | null = null;
          kernel.current?.setInventorySlice(recursiveType, (prev) => {
            const existing = prev.registry[trimmedId];
            const normalizedDetail = normalizeIncomingEntity(
              recursiveType,
              parsed.entity,
              {
                entityId: trimmedId,
                existingEntity: (existing as unknown as Record<string, unknown> | undefined) ?? null,
              }
            );
            if (!normalizedDetail) {
              return prev;
            }
            const merged = {
              ...(existing || {}),
              ...normalizedDetail,
            } as InventoryEntityState<T>;
            fetchedEntity = merged;

            return {
              ...prev,
              registry: { ...prev.registry, [trimmedId]: merged },
              lastActivity: Date.now()
            };
          });
          reconcileTouchedSlice(recursiveType, [trimmedId]);
          setEntityDetailCoverage(recursiveType as KernelEntityType, trimmedId, DETAIL_COVERAGE_FULL);

          // Keep timeline indexes canonical after detail sync (e.g., detached message sets chatSessionId=null).
          if ((recursiveType as InventoryEntityType) === 'TimelineEvent' && kernel.current) {
            const syncedTimelineEvent =
              (kernel.current.getState().inventory.TimelineEvent.registry[trimmedId] as InventoryEntityState<InventoryEntityType> | undefined) ??
              null;
            const nextSessionId = extractTimelineEventChatSessionId(syncedTimelineEvent);
            const nextSubjectKey = extractTimelineEventSubjectKey(syncedTimelineEvent);
            const reindexMutations: TimelineIndexMutation[] = [{ type: 'purge', eventId: trimmedId }];

            if (nextSessionId || nextSubjectKey) {
              reindexMutations.push({
                type: 'upsert',
                eventId: trimmedId,
                ...(nextSessionId ? { sessionId: nextSessionId } : {}),
                ...(nextSubjectKey ? { subjectKey: nextSubjectKey } : {}),
              });
            }

            applyTimelineIndexMutations(reindexMutations);
          }

          if (timelineRefs) {
            applyTimelineRefsToIndex(timelineRefs);
          }

          if (timelineRefIds.length > 0) {
            await ensureInventoryIds('TimelineEvent', timelineRefIds, { force: true }).catch((error) => {
              console.warn('[Inventory] Failed to hydrate timeline refs from detail payload.', {
                recursiveType,
                id: trimmedId,
                error,
              });
            });
          }

          // Dependencies are independent of each other, fetch them concurrently.
          // Awaiting them in sequence turned every detail payload into a chain of
          // serial round trips (and recursively so), which dominated project-open
          // and session-activation latency. Bounded by a small worker pool: this
          // recurses, so an unbounded fan-out would multiply per level and pile up
          // far more in-flight requests than the browser will run anyway.
          const pendingDependencies = parsed.dependencies.filter(
            (dependency) =>
              dependency?.id &&
              dependency?.type &&
              !(dependency.type === recursiveType && dependency.id === trimmedId)
          );
          let dependencyCursor = 0;
          const dependencyWorker = async (): Promise<void> => {
            while (dependencyCursor < pendingDependencies.length) {
              const dependency = pendingDependencies[dependencyCursor];
              dependencyCursor += 1;
              if (!dependency) continue;
              await syncRecursive(dependency.type, dependency.id, false);
            }
          };
          await Promise.all(
            Array.from(
              { length: Math.min(DEPENDENCY_SYNC_CONCURRENCY, pendingDependencies.length) },
              () => dependencyWorker()
            )
          );

          if (requestEpoch !== clearEpochRef.current) {
            if (options.requireFresh && forceFetch) throw new Error('Fresh detail sync was cancelled.');
            return null;
          }
          // Dependency hydration may receive revision-only events. Keep a strict read's
          // content and revision from the same response, never a later mixed registry row.
          if (options.requireFresh && forceFetch) return fetchedEntity;
          return (kernel.current?.getState().inventory[recursiveType].registry[trimmedId] as InventoryEntityState<T>) || null;
        } catch (error) {
          if (isAbortError(error) || requestEpoch !== clearEpochRef.current) {
            if (options.requireFresh && forceFetch) throw error;
            return null;
          }
          console.error(`[Inventory] Failed to fetch ${recursiveType} detail:`, error);
          if (options.requireFresh && forceFetch) throw error;
          return (cached as InventoryEntityState<T>) || null;
        }
      })();

      inflightDetailFetchesRef.current.set(visitKey, request);
      try {
        return await request;
      } finally {
        if (inflightDetailAbortRef.current.get(visitKey) === abortController) {
          inflightDetailAbortRef.current.delete(visitKey);
        }
        if (inflightDetailFetchesRef.current.get(visitKey) === request) {
          inflightDetailFetchesRef.current.delete(visitKey);
        }
      }
    };

    return syncRecursive(type, id.trim(), forceRootFetch) as Promise<InventoryEntityState<K> | null>;
  }, [applyTimelineIndexMutations, applyTimelineRefsToIndex, cancelAutosaveScopes, kernel, readEntityDetailCoverage, reconcileTouchedSlice, setEntityDetailCoverage]);

  const updateFactoryPreset = useCallback(async (workspaceId: string, action: 'ensure' | 'remove'): Promise<StudioWorkspaceDTO | null> => {
    const normalizedWorkspaceId = workspaceId.trim();
    if (!normalizedWorkspaceId) return null;
    const response = await fetch('/api/list/factory-preset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ workspaceId: normalizedWorkspaceId, action }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null) as { message?: unknown } | null;
      throw new Error(typeof body?.message === 'string' ? body.message : 'Factory preset could not be prepared.');
    }
    return await syncEntity('StudioWorkspace', normalizedWorkspaceId, { force: true }) as StudioWorkspaceDTO | null;
  }, [syncEntity]);

  const ensureFactoryPreset = useCallback((workspaceId: string) => updateFactoryPreset(workspaceId, 'ensure'), [updateFactoryPreset]);
  const removeFactoryPreset = useCallback((workspaceId: string) => updateFactoryPreset(workspaceId, 'remove'), [updateFactoryPreset]);

  const buildInventoryQueryParams = useCallback(<K extends InventoryEntityType>(
    entityType: K,
    options: {
      offset: number;
      limit: number;
      search?: string;
      filters?: Partial<InventoryRegistry[K]['filters']>;
    }
  ) => {
    const k = kernel.current;
    if (!k) return new URLSearchParams();
    const params = new URLSearchParams();

    params.set('limit', String(Math.max(1, options.limit)));
    params.set('offset', String(Math.max(0, options.offset)));

    const filters = (options.filters ?? {}) as Record<string, unknown>;
    Object.keys(filters)
      .sort((left, right) => left.localeCompare(right))
      .forEach((key) => {
        if (
          key === 'organisationId' &&
          INVENTORY_GLOBAL_SCOPE_ONLY_QUERY_ENTITY_SET.has(entityType)
        ) {
          return;
        }
        if (key === 'focusIds' && filters.ids !== undefined && filters.ids !== null) return;
        const normalized = normalizeQueryValue(filters[key]);
        if (normalized === null) return;
        const queryKey = key === 'focusIds' ? 'ids' : key;
        params.set(queryKey, normalized);
      });

    if (options.search) params.set('search', options.search.trim());
    return params;
  }, [kernel]);

  const extractResponseIds = useCallback(<K extends InventoryEntityType>(
    entity: K,
    payload: LeasingDataResponse
  ): string[] => {
    const pick = (rows: Array<{ id: string }> | undefined): string[] =>
      Array.isArray(rows) ? rows.map((row) => row.id).filter(Boolean) : [];

    // Same pre-existing debt as above: the three unmatched types have no rows in
    // this payload, and the function returns [] for them by falling through.
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (entity) {
      case 'Property':
        return pick(payload.properties as Array<{ id: string }> | undefined);
      case 'Floor':
        return pick(payload.floors as Array<{ id: string }> | undefined);
      case 'Unit':
        return pick(payload.units as Array<{ id: string }> | undefined);
      case 'Brand':
        return pick(payload.brands as Array<{ id: string }> | undefined);
      case 'Portfolio':
        return pick(payload.portfolios as Array<{ id: string }> | undefined);
      case 'LegalEntity':
        return pick(payload.legalEntities as Array<{ id: string }> | undefined);
      case 'Deal':
        return pick(payload.deals as Array<{ id: string }> | undefined);
      case 'MarketSignal':
        return pick(payload.signals as Array<{ id: string }> | undefined);
      case 'Audit':
        return pick(payload.audits as Array<{ id: string }> | undefined);
      case 'ChatSession':
        return pick(payload.chatSessions as Array<{ id: string }> | undefined);
      case 'TimelineEvent':
        return pick(payload.timelineEvents as Array<{ id: string }> | undefined);
      case 'Campaign':
        return pick(payload.campaigns as Array<{ id: string }> | undefined);
      case 'SourcingCampaign':
        return pick(payload.sourcingCampaigns as Array<{ id: string }> | undefined);
      case 'PortfolioFitCampaign':
        return pick(payload.portfolioFitCampaigns as Array<{ id: string }> | undefined);
      case 'PortfolioFit':
        return pick(payload.portfolioFits as Array<{ id: string }> | undefined);
      case 'Lead':
        return pick(payload.leads as Array<{ id: string }> | undefined);
      case 'Project':
        return pick(payload.projects as Array<{ id: string }> | undefined);
      case 'Organisation':
        return pick(payload.organisations as Array<{ id: string }> | undefined);
      case 'CreditGrant':
        return pick(payload.creditGrants as Array<{ id: string }> | undefined);
      case 'SlideCorrection':
        return pick(payload.slideCorrections as Array<{ id: string }> | undefined);
      case 'Team':
        return pick(payload.teams as Array<{ id: string }> | undefined);
      case 'User':
        return pick(payload.users as Array<{ id: string }> | undefined);
      case 'Notification':
        return pick(payload.notifications as Array<{ id: string }> | undefined);
      case 'Contact':
        return pick(payload.contacts as Array<{ id: string }> | undefined);
      case 'UniversalFact':
        return pick(payload.facts as Array<{ id: string }> | undefined);
      case 'StudioWorkspace':
        return pick(payload.workspaces as Array<{ id: string }> | undefined);
      case 'StudioLayer':
        return pick(payload.layers as Array<{ id: string }> | undefined);
      case 'StudioWorkspaceVersion':
        return pick(payload.workspaceVersions as Array<{ id: string }> | undefined);
      default:
        return [];
    }
  }, []);

  const refreshInventory = useCallback(async <K extends InventoryEntityType>(
    entity: K,
    filters?: Partial<InventoryRegistry[K]['filters']>,
    limit?: number,
    options: InventoryRefreshOptions = {}
  ) => {
    const k = kernel.current;
    if (!k) return;

    const slice = k.getState().inventory[entity];
    const endpoint = INVENTORY_ENDPOINTS[entity];
    if (!endpoint) throw new Error(`No endpoint for ${entity}`);

    const effectiveFilters = ((filters ?? {}) as Record<string, unknown>) as Partial<InventoryRegistry[K]['filters']>;
    
    const effectiveLimit = limit ?? 100;
    
    // 1. Compute Signature
    const signature = computeQuerySignature(
      entity,
      effectiveFilters as Record<string, unknown>,
      { limit: effectiveLimit, offset: 0 }
    );

    const ttl = options.ttl ?? DEFAULT_QUERY_TTL_MS;
    const now = Date.now();
    const cachedEntry = slice.queryCache?.[signature];

    // 2. CACHE HIT CHECK
    if (!options.force && cachedEntry && (now - cachedEntry.timestamp < ttl)) {
      return; // Done! View is restored without network.
    }

    // 3. IN-FLIGHT DEDUPLICATION
    const inflight = inflightRefreshesRef.current.get(signature);
    if (!options.force && inflight) {
      await inflight;
      return;
    }

    // 4. NETWORK FETCH
    const requestEpoch = clearEpochRef.current;
    const abortController = new AbortController();
    inflightRefreshAbortRef.current.set(signature, abortController);
    const request = (async () => {
      k.setInventorySlice(entity, (prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const params = buildInventoryQueryParams(entity, {
          offset: 0,
          limit: effectiveLimit,
          filters: effectiveFilters
        });
        
        const response = await fetch(`${endpoint}?${params.toString()}`, {
          method: 'GET',
          cache: 'no-store',
          signal: abortController.signal,
        });
        if (requestEpoch !== clearEpochRef.current) {
          return;
        }
        if (!response.ok) throw new Error(await response.text().catch(() => '') || `Status ${response.status}`);
        
        const payload = await response.json();
        if (requestEpoch !== clearEpochRef.current) {
          return;
        }
        if (payload.pagination) payload.pagination.mode = 'replace';

        // Update Registry & View
        hydrateInventoryData(payload);

        // 5. CACHE WRITE
        const resultIds = extractResponseIds(entity, payload);
        const resultTotal = payload.pagination?.total ?? resultIds.length;

        k.setInventorySlice(entity, (prev) => ({
          ...prev,
          queryCache: {
            ...(prev.queryCache || {}),
            [signature]: { 
              timestamp: Date.now(),
              ids: resultIds,
              total: resultTotal
            }
          }
        }));

      } catch (error: any) {
        if (isAbortError(error) || requestEpoch !== clearEpochRef.current) {
          return;
        }
        k.setInventorySlice(entity, (prev) => ({ ...prev, error: error.message || 'Error' }));
        if (options.throwOnError) {
          throw error;
        }
      } finally {
        if (requestEpoch === clearEpochRef.current) {
          k.setInventorySlice(entity, (prev) => ({ ...prev, isLoading: false }));
        }
      }
    })();

    inflightRefreshesRef.current.set(signature, request);
    try {
      await request;
    } finally {
      if (inflightRefreshAbortRef.current.get(signature) === abortController) {
        inflightRefreshAbortRef.current.delete(signature);
      }
      if (inflightRefreshesRef.current.get(signature) === request) {
        inflightRefreshesRef.current.delete(signature);
      }
    }
  }, [kernel, buildInventoryQueryParams, hydrateInventoryData, extractResponseIds]);

  const queryInventory = useCallback(async <K extends InventoryEntityType>(
    entity: K,
    options: {
      filters?: Partial<InventoryRegistry[K]['filters']>;
      limit?: number;
      offset?: number;
      force?: boolean;
      ttl?: number;
      skipRows?: ReadonlySet<string>;
    } = {}
  ): Promise<{ ids: string[]; pagination: Pagination<K> | null; error: string | null }> => {
    const k = kernel.current;
    if (!k) return { ids: [], pagination: null, error: 'Kernel unavailable' };

    const slice = k.getState().inventory[entity];
    const endpoint = INVENTORY_ENDPOINTS[entity];
    if (!endpoint) return { ids: [], pagination: null, error: `No endpoint for ${entity}` };

    const effectiveFilters = {
      ...(((options.filters as Record<string, unknown> | undefined) ?? {})),
    } as Partial<InventoryRegistry[K]['filters']>;
    const effectiveLimit = options.limit ?? 100;
    const effectiveOffset = Math.max(0, options.offset ?? 0);
    const ttl = options.ttl ?? DEFAULT_QUERY_TTL_MS;
    const signature = computeQuerySignature(
      entity,
      effectiveFilters as Record<string, unknown>,
      { limit: effectiveLimit, offset: effectiveOffset }
    );
    const now = Date.now();
    const cachedEntry = slice.queryCache?.[signature];

    if (!options.force && cachedEntry && (now - cachedEntry.timestamp < ttl)) {
      const ids = cachedEntry.ids.filter((id) => Boolean(k.getState().inventory[entity].registry[id]));
      const pagination = {
        mode: effectiveOffset > 0 ? 'append' : 'replace',
        limit: effectiveLimit,
        offset: effectiveOffset,
        total: cachedEntry.total,
        hasMore: effectiveOffset + ids.length < cachedEntry.total
      } as Pagination<K>;
      return { ids, pagination, error: null };
    }

    const inflight = inflightQueriesRef.current.get(signature);
    if (!options.force && inflight) {
      return inflight as Promise<{ ids: string[]; pagination: Pagination<K> | null; error: string | null }>;
    }

    const requestEpoch = clearEpochRef.current;
    const abortController = new AbortController();
    inflightQueriesAbortRef.current.set(signature, abortController);
    const request = (async () => {
      try {
        const params = buildInventoryQueryParams(entity, {
          offset: effectiveOffset,
          limit: effectiveLimit,
          filters: effectiveFilters
        });
        const response = await fetch(`${endpoint}?${params.toString()}`, {
          method: 'GET',
          cache: 'no-store',
          signal: abortController.signal,
        });
        if (requestEpoch !== clearEpochRef.current) {
          return { ids: [], pagination: null, error: null };
        }
        if (!response.ok) throw new Error(await response.text().catch(() => '') || `Status ${response.status}`);

        const payload = await response.json() as LeasingDataResponse;
        if (requestEpoch !== clearEpochRef.current) {
          return { ids: [], pagination: null, error: null };
        }
        hydrateInventoryData(payload, options.skipRows);

        const ids = extractResponseIds(entity, payload);
        const total = payload.pagination?.total ?? ids.length;
        const pagination = {
          mode: payload.pagination?.mode ?? (effectiveOffset > 0 ? 'append' : 'replace'),
          limit: payload.pagination?.limit ?? effectiveLimit,
          offset: payload.pagination?.offset ?? effectiveOffset,
          total,
          hasMore: payload.pagination?.hasMore ?? ((effectiveOffset + ids.length) < total),
          aggregations: payload.pagination?.aggregations
        } as Pagination<K>;

        k.setInventorySlice(entity, (prev) => ({
          ...prev,
          queryCache: {
            ...(prev.queryCache || {}),
            [signature]: {
              timestamp: Date.now(),
              ids,
              total
            }
          }
        }));

        return { ids, pagination, error: null };
      } catch (error: any) {
        if (isAbortError(error) || requestEpoch !== clearEpochRef.current) {
          return { ids: [], pagination: null, error: null };
        }
        return { ids: [], pagination: null, error: error.message || 'Error' };
      }
    })();

    inflightQueriesRef.current.set(signature, request as Promise<{ ids: string[]; pagination: Pagination<any> | null; error: string | null }>);
    try {
      return await request;
    } finally {
      if (inflightQueriesAbortRef.current.get(signature) === abortController) {
        inflightQueriesAbortRef.current.delete(signature);
      }
      if (inflightQueriesRef.current.get(signature) === request) {
        inflightQueriesRef.current.delete(signature);
      }
    }
  }, [kernel, buildInventoryQueryParams, extractResponseIds, hydrateInventoryData]);

  const resolveRequestedOrganisationId = useCallback((
    organisationIdOverride?: string | null
  ): string => {
    const k = kernel.current;
    if (!k) return '';
    if (typeof organisationIdOverride === 'string') {
      return organisationIdOverride.trim();
    }
    const { userContext } = k.getState();
    if (userContext.isFaterAdmin) {
      return '';
    }
    return typeof userContext.organisationId === 'string'
      ? userContext.organisationId.trim()
      : '';
  }, [kernel]);

  const markInventoryStatsStale = useCallback((
    entity: InventoryStatsEntityType,
    options: {
      scopeKey?: string;
      organisationId?: string | null;
    } = {}
  ) => {
    const k = kernel.current;
    if (!k) return;
    if (!INVENTORY_STATS_SUPPORTED_ENTITY_TYPE_SET.has(entity)) return;

    const explicitScopeKey =
      typeof options.scopeKey === 'string' ? options.scopeKey.trim() : '';
    const resolvedScopeKey = explicitScopeKey ||
      (options.organisationId !== undefined
        ? buildInventoryStatsScopeKey(
            typeof options.organisationId === 'string' ? options.organisationId.trim() : ''
          )
        : '');

    k.setState((prevState) => {
      const currentEntityStats = prevState.inventoryStats[entity] ?? {};

      if (resolvedScopeKey) {
        const existingEntry = currentEntityStats[resolvedScopeKey];
        if (!existingEntry) return prevState;
        if (existingEntry.stale) return prevState;

        return {
          ...prevState,
          inventoryStats: {
            ...prevState.inventoryStats,
            [entity]: {
              ...currentEntityStats,
              [resolvedScopeKey]: {
                ...existingEntry,
                stale: true,
              },
            },
          },
        };
      }

      const keys = Object.keys(currentEntityStats);
      if (keys.length === 0) return prevState;

      let changed = false;
      const nextEntityStats: Record<string, InventoryStatsEntry> = { ...currentEntityStats };
      keys.forEach((key) => {
        const entry = currentEntityStats[key];
        if (!entry || entry.stale) return;
        nextEntityStats[key] = {
          ...entry,
          stale: true,
        };
        changed = true;
      });

      if (!changed) return prevState;

      return {
        ...prevState,
        inventoryStats: {
          ...prevState.inventoryStats,
          [entity]: nextEntityStats,
        },
      };
    });
  }, [kernel]);

  const invalidateInventoryStats = useCallback((
    entity: InventoryStatsEntityType,
    options: {
      scopeKey?: string;
      organisationId?: string | null;
    } = {}
  ) => {
    markInventoryStatsStale(entity, options);
  }, [markInventoryStatsStale]);

  const fetchInventoryStats = useCallback(async (
    entity: InventoryStatsEntityType,
    options: {
      force?: boolean;
      ttlMs?: number;
      signal?: AbortSignal;
      organisationId?: string | null;
      scopeKey?: string;
      queryParams?: Record<string, string | number | boolean | null | undefined>;
      endpointOverride?: string;
      includeOrganisationIdParam?: boolean;
    } = {}
  ): Promise<void> => {
    const k = kernel.current;
    if (!k) return;
    if (!INVENTORY_STATS_SUPPORTED_ENTITY_TYPE_SET.has(entity)) return;

    const endpoint =
      typeof options.endpointOverride === 'string' && options.endpointOverride.trim().length > 0
        ? options.endpointOverride.trim()
        : INVENTORY_STATS_ENDPOINTS[entity];
    if (!endpoint) {
      throw new Error(`No stats endpoint configured for ${entity}.`);
    }

    const enforceGlobalScope = INVENTORY_GLOBAL_SCOPE_ONLY_STATS_ENTITY_SET.has(entity);
    const includeOrganisationIdParam = !enforceGlobalScope && options.includeOrganisationIdParam !== false;
    const requestedOrganisationId = includeOrganisationIdParam
      ? resolveRequestedOrganisationId(options.organisationId)
      : '';
    const explicitScopeKey =
      enforceGlobalScope
        ? ''
        : (typeof options.scopeKey === 'string' ? options.scopeKey.trim() : '');
    const scopeKey = enforceGlobalScope
      ? INVENTORY_STATS_GLOBAL_SCOPE_KEY
      : (explicitScopeKey || buildInventoryStatsScopeKey(requestedOrganisationId));
    const cacheKey = buildInventoryStatsCacheKey(entity, scopeKey);
    const ttlMs = options.ttlMs ?? DEFAULT_ENTITY_STATS_TTL_MS;
    const force = options.force === true;

    const readStatsEntry = (): InventoryStatsEntry | null =>
      k.getState().inventoryStats[entity]?.[scopeKey] ?? null;

    const cached = readStatsEntry();
    if (
      !force &&
      cached &&
      !cached.stale &&
      Date.now() - cached.timestamp <= ttlMs
    ) {
      return;
    }

    const inflight = inflightStatsFetchesRef.current.get(cacheKey);
    if (!force && inflight) {
      await inflight;
      return;
    }

    const requestEpoch = clearEpochRef.current;
    const ownerId = k.getUserContext().dbUserId;
    const wasAdmin = k.getUserContext().isFaterAdmin;

    k.setState((prevState) => {
      const entityStats = prevState.inventoryStats[entity] ?? {};
      const previousEntry = entityStats[scopeKey] ?? createDefaultInventoryStatsEntry();
      return {
        ...prevState,
        inventoryStats: {
          ...prevState.inventoryStats,
          [entity]: {
            ...entityStats,
            [scopeKey]: {
              ...previousEntry,
              isLoading: true,
              error: null,
            },
          },
        },
      };
    });

    const request = (async (): Promise<void> => {
      try {
        const searchParams = new URLSearchParams();
        if (includeOrganisationIdParam && requestedOrganisationId) {
          searchParams.set('organisationId', requestedOrganisationId);
        }
        const queryParams = options.queryParams ?? {};
        Object.entries(queryParams).forEach(([key, rawValue]) => {
          if (!key) return;
          if (rawValue === undefined || rawValue === null) return;
          searchParams.set(key, String(rawValue));
        });
        const requestUrl = searchParams.size > 0
          ? `${endpoint}?${searchParams.toString()}`
          : endpoint;

        const response = await fetch(requestUrl, {
          method: 'GET',
          cache: 'no-store',
          signal: options.signal,
        });
        if (!response.ok) {
          throw new Error(await response.text().catch(() => '') || `Status ${response.status}`);
        }
        const payload = (await response.json()) as Partial<TableStatsResponseV1> | null;
        if (requestEpoch !== clearEpochRef.current || ownerId !== k.getUserContext().dbUserId || wasAdmin !== k.getUserContext().isFaterAdmin) return;
        options.signal?.throwIfAborted();
        if (entity === 'Brand' && wasAdmin &&
          (options.queryParams?.originBuckets === true || payload?.originBuckets !== undefined || cached?.data?.originBuckets !== undefined)) {
          const fields = ['brands', 'stores', 'withStores', 'registryMatched', 'withWebsite', 'categorised', 'usedByClients', 'liquidated', 'officialRevenue', 'estimatedRevenue', 'expansionSourced', 'expanding'];
          if (!Array.isArray(payload?.originBuckets) || !payload.originBuckets.every((bucket: unknown) =>
            isRecord(bucket) && typeof bucket.origin === 'string' && bucket.origin.trim().length > 0 &&
            fields.every((field) => typeof bucket[field] === 'number' && Number.isFinite(bucket[field]) && (bucket[field] as number) >= 0))) {
            throw new Error('Brand source metrics are unavailable');
          }
        }
        const normalizedPayload: TableStatsResponseV1 = {
          version: 'v1',
          entityType: typeof payload?.entityType === 'string' ? payload.entityType : entity,
          scopeKey:
            typeof payload?.scopeKey === 'string' && payload.scopeKey.trim().length > 0
              ? payload.scopeKey
              : scopeKey,
          computedAt:
            typeof payload?.computedAt === 'string'
              ? payload.computedAt
              : new Date().toISOString(),
          executionMs:
            typeof payload?.executionMs === 'number' && Number.isFinite(payload.executionMs)
              ? payload.executionMs
              : 0,
          isPartial: payload?.isPartial === true,
          cards: Array.isArray(payload?.cards) ? payload.cards : [],
          sections: Array.isArray(payload?.sections) ? payload.sections : undefined,
          ...(entity === 'Brand' && wasAdmin && Array.isArray(payload?.originBuckets) ? { originBuckets: payload.originBuckets } : {}),
        };
        if (entity === 'Brand' &&
          (options.queryParams?.revenueCoverage === true || normalizedPayload.sections?.some((section) => section?.id === 'revenue_coverage')) &&
          !readBrandRevenueCoverage(normalizedPayload)) {
          throw new Error('Revenue coverage is unavailable');
        }

        k.setState((prevState) => {
          const entityStats = prevState.inventoryStats[entity] ?? {};
          const previousEntry = entityStats[scopeKey] ?? createDefaultInventoryStatsEntry();
          return {
            ...prevState,
            inventoryStats: {
              ...prevState.inventoryStats,
              [entity]: {
                ...entityStats,
                [scopeKey]: {
                  ...previousEntry,
                  data: normalizedPayload,
                  isLoading: false,
                  error: null,
                  timestamp: Date.now(),
                  stale: false,
                },
              },
            },
          };
        });
      } catch (error) {
        if (requestEpoch !== clearEpochRef.current || ownerId !== k.getUserContext().dbUserId || wasAdmin !== k.getUserContext().isFaterAdmin) return;
        const aborted = options.signal?.aborted || isAbortError(error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to fetch inventory stats.';
        k.setState((prevState) => {
          const entityStats = prevState.inventoryStats[entity] ?? {};
          const previousEntry = entityStats[scopeKey] ?? createDefaultInventoryStatsEntry();
          return {
            ...prevState,
            inventoryStats: {
              ...prevState.inventoryStats,
              [entity]: {
                ...entityStats,
                [scopeKey]: {
                  ...previousEntry,
                  isLoading: false,
                  error: aborted ? previousEntry.error : errorMessage,
                  stale: aborted ? previousEntry.stale : true,
                },
              },
            },
          };
        });
        if (!aborted) throw error;
      }
    })();

    inflightStatsFetchesRef.current.set(cacheKey, request);
    try {
      await request;
    } finally {
      if (inflightStatsFetchesRef.current.get(cacheKey) === request) {
        inflightStatsFetchesRef.current.delete(cacheKey);
        if (requestEpoch === clearEpochRef.current && (ownerId !== k.getUserContext().dbUserId || wasAdmin !== k.getUserContext().isFaterAdmin)) {
          k.setState((prevState) => ({
            ...prevState,
            inventoryStats: {
              ...prevState.inventoryStats,
              [entity]: { ...prevState.inventoryStats[entity], [scopeKey]: createDefaultInventoryStatsEntry() },
            },
          }));
        }
      }
    }
  }, [kernel, resolveRequestedOrganisationId]);

  const fetchInventoryCount = useCallback(async (
    entity: InventoryCountableEntityType,
    options: {
      force?: boolean;
      ttlMs?: number;
      signal?: AbortSignal;
      organisationId?: string | null;
    } = {}
  ): Promise<void> => {
    const k = kernel.current;
    if (!k) return;

    const endpoint = INVENTORY_COUNT_ENDPOINTS[entity];
    if (!endpoint) {
      throw new Error(`No count endpoint configured for ${entity}.`);
    }

    const requestedOrganisationId = INVENTORY_GLOBAL_SCOPE_ONLY_COUNT_ENTITY_SET.has(entity)
      ? ''
      : resolveRequestedOrganisationId(options.organisationId);
    const scopeKey = buildInventoryCountScopeKey(requestedOrganisationId);
    const cacheKey = buildInventoryCountCacheKey(entity, scopeKey);
    const ttlMs = options.ttlMs ?? DEFAULT_ENTITY_COUNT_TTL_MS;
    const force = options.force === true;

    const readCountEntry = (): InventoryCountEntry | null =>
      k.getState().inventoryCounts[entity]?.[scopeKey] ?? null;

    const cached = readCountEntry();
    if (!force && cached && Date.now() - cached.timestamp <= ttlMs) {
      return;
    }

    const inflight = inflightCountFetchesRef.current.get(cacheKey);
    if (!force && inflight) {
      await inflight;
      return;
    }

    k.setState((prevState) => {
      const entityCounts = prevState.inventoryCounts[entity] ?? {};
      const previousEntry = entityCounts[scopeKey] ?? createDefaultInventoryCountEntry();
      return {
        ...prevState,
        inventoryCounts: {
          ...prevState.inventoryCounts,
          [entity]: {
            ...entityCounts,
            [scopeKey]: {
              ...previousEntry,
              isLoading: true,
              error: null,
            },
          },
        },
      };
    });

    const request = (async (): Promise<void> => {
      try {
        const searchParams = new URLSearchParams();
        if (requestedOrganisationId) {
          searchParams.set('organisationId', requestedOrganisationId);
        }
        const requestUrl = searchParams.size > 0
          ? `${endpoint}?${searchParams.toString()}`
          : endpoint;

        const response = await fetch(requestUrl, {
          method: 'GET',
          cache: 'no-store',
          signal: options.signal,
        });
        if (!response.ok) {
          throw new Error(await response.text().catch(() => '') || `Status ${response.status}`);
        }
        const payload = (await response.json()) as { count?: unknown };
        const nextCount =
          typeof payload?.count === 'number' && Number.isFinite(payload.count)
            ? Math.max(0, Math.floor(payload.count))
            : 0;
        k.setState((prevState) => {
          const entityCounts = prevState.inventoryCounts[entity] ?? {};
          const previousEntry = entityCounts[scopeKey] ?? createDefaultInventoryCountEntry();
          return {
            ...prevState,
            inventoryCounts: {
              ...prevState.inventoryCounts,
              [entity]: {
                ...entityCounts,
                [scopeKey]: {
                  ...previousEntry,
                  count: nextCount,
                  isLoading: false,
                  error: null,
                  timestamp: Date.now(),
                },
              },
            },
          };
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to fetch inventory count.';
        k.setState((prevState) => {
          const entityCounts = prevState.inventoryCounts[entity] ?? {};
          const previousEntry = entityCounts[scopeKey] ?? createDefaultInventoryCountEntry();
          return {
            ...prevState,
            inventoryCounts: {
              ...prevState.inventoryCounts,
              [entity]: {
                ...entityCounts,
                [scopeKey]: {
                  ...previousEntry,
                  isLoading: false,
                  error: errorMessage,
                },
              },
            },
          };
        });
        throw error;
      }
    })();

    inflightCountFetchesRef.current.set(cacheKey, request);
    try {
      await request;
    } finally {
      if (inflightCountFetchesRef.current.get(cacheKey) === request) {
        inflightCountFetchesRef.current.delete(cacheKey);
      }
    }
  }, [kernel, resolveRequestedOrganisationId]);

  const ensureInventoryIds = useCallback(async <K extends InventoryEntityType>(
    entity: K,
    ids: string[],
    options: { batchSize?: number; force?: boolean } = {}
  ) => {
    const k = kernel.current;
    if (!k) {
      return {
        requested: 0,
        missing: 0,
        hydrated: 0,
        unresolvedIds: [],
        errors: ['Kernel unavailable'],
      };
    }

    const normalizedIds = Array.from(
      new Set(
        (Array.isArray(ids) ? ids : [])
          .filter((id): id is string => typeof id === 'string')
          .map((id) => id.trim())
          .filter((id) => id.length > 0)
      )
    );

    if (normalizedIds.length === 0) {
      return {
        requested: 0,
        missing: 0,
        hydrated: 0,
        unresolvedIds: [],
        errors: [],
      };
    }

    const registry = k.getState().inventory[entity].registry as Record<string, unknown>;
    const missingIds = normalizedIds.filter((id) => !registry[id]);
    if (missingIds.length === 0) {
      return {
        requested: normalizedIds.length,
        missing: 0,
        hydrated: normalizedIds.length,
        unresolvedIds: [],
        errors: [],
      };
    }

    const force = options.force ?? true;
    const batchSize = options.batchSize ?? DEFAULT_IDS_ENSURE_BATCH_SIZE;
    const idBatches = chunkStringArray(missingIds, batchSize);
    const errors: string[] = [];

    // Each batch is a disjoint set of ids, so run them concurrently rather than
    // waiting for one page of hydration before requesting the next.
    const batchResults = await Promise.all(
      idBatches.map((batch) =>
        queryInventory(entity, {
          filters: { ids: batch } as Partial<InventoryRegistry[K]['filters']>,
          limit: batch.length,
          offset: 0,
          force,
          ttl: 0,
        })
      )
    );
    batchResults.forEach((result) => {
      if (result.error) errors.push(result.error);
    });

    const refreshedRegistry = k.getState().inventory[entity].registry as Record<string, unknown>;
    const unresolvedIds = missingIds.filter((id) => !refreshedRegistry[id]);

    return {
      requested: normalizedIds.length,
      missing: missingIds.length,
      hydrated: missingIds.length - unresolvedIds.length,
      unresolvedIds,
      errors,
    };
  }, [kernel, queryInventory]);

  const clearInventory = useCallback(() => {
    const k = kernel.current;
    if (!k) return;
    clearEpochRef.current += 1;

    const inventory = k.getState().inventory as Record<string, any>;
    const nextInventory: Record<string, any> = { ...inventory };
    const clearedSliceKeys: string[] = [];
    const clearedAt = Date.now();

    inflightRefreshAbortRef.current.forEach((controller) => controller.abort());
    inflightQueriesAbortRef.current.forEach((controller) => controller.abort());
    inflightDetailAbortRef.current.forEach((controller) => controller.abort());
    inflightRefreshAbortRef.current.clear();
    inflightQueriesAbortRef.current.clear();
    inflightDetailAbortRef.current.clear();
    inflightRefreshesRef.current.clear();
    inflightQueriesRef.current.clear();
    inflightDetailFetchesRef.current.clear();
    inflightCountFetchesRef.current.clear();
    inflightStatsFetchesRef.current.clear();

    autosaveQueueRef.current.forEach((entry) => {
      if (entry.timer) {
        clearTimeout(entry.timer);
      }
    });
    autosaveQueueRef.current.clear();
    sentAutosaveRecordsRef.current.clear();
    lastSentFieldValuesRef.current.clear();
    if (autosaveMutationBatchTimerRef.current) {
      clearTimeout(autosaveMutationBatchTimerRef.current);
      autosaveMutationBatchTimerRef.current = null;
    }
    autosaveMutationBatchQueueRef.current.forEach((entry) => {
      entry.reject(new Error('Inventory cleared before batched autosave mutation flush.'));
    });
    autosaveMutationBatchQueueRef.current = [];
    autosaveMutationBatchInFlightRef.current = false;
    autosaveMutationBatchStallsRef.current = 0;
    if (studioWriteSuspendedSinceRef.current !== null) toast.dismiss('studio-write-held');
    studioWriteSuspendedSinceRef.current = null;

    Object.entries(inventory).forEach(([sliceKey, slice]) => {
      if (!slice || typeof slice !== 'object' || Array.isArray(slice)) {
        return;
      }

      const hasRegistry = Object.prototype.hasOwnProperty.call(slice, 'registry');
      const hasQueryCache = Object.prototype.hasOwnProperty.call(slice, 'queryCache');
      if (!hasRegistry || !hasQueryCache) {
        return;
      }

      nextInventory[sliceKey] = {
        ...slice,
        registry: {},
        queryCache: {},
        queryErrors: {},
        isLoading: false,
        error: null,
        lastActivity: clearedAt,
        lastCreateActivity: 0,
      };
      clearedSliceKeys.push(sliceKey);
    });

    k.setState((prevState) => ({
      ...prevState,
      activeChatSessionId: null,
      inventory: nextInventory as any,
      timelineFeedIndex: {
        bySessionId: {},
        bySubjectKey: {},
      },
      timelineFeedState: {},
      inventoryCounts: createEmptyInventoryCounts(),
      inventoryStats: createEmptyInventoryStats(),
      entityKernelState: {},
    }));
    console.log('[Inventory] Clearing local registries and query caches.', {
      clearedSliceKeys,
    });
  }, [kernel]);

  const initializeInventory = useCallback(async () => {
    const k = kernel.current;
    if (!k) return;

    console.log("[Inventory] Initializing Inventory for current user context...");
    k.setInventoryInitStatus('initializing');
    const startTime = performance.now();

    try {
      // 1. Cold-start reset
      clearInventory();

      // 2. Apply defaults
      const userCtx = k.getUserContext();
      const userId = userCtx.dbUserId;
      const activeChatSessionId = k.getActiveChatSessionId();

      const tasks: Promise<void>[] = [];
      tasks.push(refreshInventory('Project', userId ? ({ userId } as any) : undefined, 16, { throwOnError: true }));
      
      tasks.push(refreshInventory('Property', { minUnits: 1 }, undefined, { throwOnError: true }));
      const chatSessionRefreshTask = refreshInventory(
        'ChatSession',
        userId ? { userId } : undefined,
        100,
        { throwOnError: true }
      );
      tasks.push(chatSessionRefreshTask);
      // The left bar's Recents lists only Daimon and Master conversations, and the 100 newest of
      // every kind are mostly Studio chats (93 of 100 on 2026-10-04), so Recents came up empty:
      // its own conversations are fetched by module.
      if (userId) for (const creationModule of ['daimon', 'master'] as const) {
        tasks.push(refreshInventory('ChatSession', { userId, creationModule } as never, 50, { throwOnError: true }));
      }
      if (activeChatSessionId) {
        tasks.push(chatSessionRefreshTask.then(async () => {
          const hasActiveSession =
            !!k.getState().inventory.ChatSession.registry[activeChatSessionId];
          if (!hasActiveSession) {
            // Avoid pointless 404 detail polling for stale session ids.
            if (k.getActiveChatSessionId() === activeChatSessionId) {
              k.setState({ activeChatSessionId: null });
            }
            return;
          }
          await syncEntity('ChatSession', activeChatSessionId).then(() => {});
        }));
      }
      tasks.push(refreshInventory('Organisation', undefined, undefined, { throwOnError: true }));
      tasks.push(refreshInventory('User', undefined, undefined, { throwOnError: true }));
      
      await Promise.all(tasks);
      
      const endTime = performance.now();
      const inv = k.getState().inventory;
      
      const stats = {
        properties: Object.keys(inv.Property.registry).length,
        units: Object.keys(inv.Unit.registry).length,
        deals: Object.keys(inv.Deal.registry).length,
        sourcingCampaigns: Object.keys(inv.SourcingCampaign.registry).length,
        portfolios: Object.keys(inv.Portfolio.registry).length,
        audits: Object.keys(inv.Audit.registry).length
      }

      console.log(`[Inventory] Initialization complete in ${(endTime - startTime).toFixed(0)}ms.`, stats);
      k.setInventoryInitStatus('initialized');
    } catch (error) {
      console.error('[Inventory] Critical failure during initialization:', error);
      k.handleError('Failed to load your inventory dashboard.');
      k.setInventoryInitStatus('initialized');
    }
  }, [kernel, refreshInventory, clearInventory, syncEntity]);

  const reconnectInventory = useCallback(async () => {
    const k = kernel.current;
    if (!k) return;

    try {
      const startTime = performance.now();
      const userCtx = k.getUserContext();
      const userId = userCtx.dbUserId;
      const activeChatSessionId = k.getActiveChatSessionId();
      const tasks: Promise<void>[] = [];

      tasks.push(refreshInventory('Project', userId ? ({ userId } as any) : undefined, 16, {
        throwOnError: true,
        force: true,
      }));

      tasks.push(refreshInventory('Property', { minUnits: 1 }, undefined, {
        throwOnError: true,
        force: true,
      }));
      const chatSessionRefreshTask = refreshInventory('ChatSession', userId ? { userId } : undefined, 100, {
        throwOnError: true,
        force: true,
      });
      tasks.push(chatSessionRefreshTask);
      // The left bar's Recents lists only Daimon and Master conversations, and the 100 newest of
      // every kind are mostly Studio chats (93 of 100 on 2026-10-04), so Recents came up empty:
      // its own conversations are fetched by module.
      if (userId) for (const creationModule of ['daimon', 'master'] as const) {
        tasks.push(refreshInventory('ChatSession', { userId, creationModule } as never, 50, { throwOnError: true, force: true }));
      }
      if (activeChatSessionId) {
        tasks.push(chatSessionRefreshTask.then(async () => {
          const hasActiveSession =
            !!k.getState().inventory.ChatSession.registry[activeChatSessionId];
          if (!hasActiveSession) {
            if (k.getActiveChatSessionId() === activeChatSessionId) {
              k.setState({ activeChatSessionId: null });
            }
            return;
          }
          await syncEntity('ChatSession', activeChatSessionId).then(() => {});
        }));
      }

      tasks.push(refreshInventory('Organisation', undefined, undefined, {
        throwOnError: true,
        force: true,
      }));
      tasks.push(refreshInventory('User', undefined, undefined, {
        throwOnError: true,
        force: true,
      }));

      await Promise.all(tasks);
      const endTime = performance.now();
      console.log(`[Inventory] Reconnect catch-up complete in ${(endTime - startTime).toFixed(0)}ms.`);
    } catch (error) {
      console.error('[Inventory] Failed to catch up after socket reconnect:', error);
    }
  }, [kernel, refreshInventory, syncEntity]);


  const applyMutationResultsFallback = useCallback((results: InventoryMutationSuccess[], socketConnectionAtSend: string | null = null) => {
    const touchedCountEntities = new Set<InventoryCountableEntityType>();
    const touchedStatsEntities = new Set<InventoryStatsEntityType>();
    results.forEach((result) => {
      if (INVENTORY_COUNTABLE_ENTITY_TYPES.includes(result.entity as InventoryCountableEntityType)) {
        touchedCountEntities.add(result.entity as InventoryCountableEntityType);
      }
      const statsEntity = INVENTORY_ENTITY_TO_STATS_ENTITY[result.entity as InventoryEntityType];
      if (statsEntity) {
        touchedStatsEntities.add(statsEntity);
      }
    });

    if (touchedCountEntities.size > 0) {
      const k = kernel.current;
      if (k) {
        k.setState((prevState) => {
          let changed = false;
          const nextInventoryCounts = { ...prevState.inventoryCounts };
          touchedCountEntities.forEach((entity) => {
            const entityCounts = prevState.inventoryCounts[entity] ?? {};
            const keys = Object.keys(entityCounts);
            if (keys.length === 0) return;

            const nextEntityCounts: Record<string, InventoryCountEntry> = { ...entityCounts };
            keys.forEach((cacheKey) => {
              const entry = entityCounts[cacheKey];
              if (!entry) return;
              nextEntityCounts[cacheKey] = {
                ...entry,
                timestamp: 0,
                error: null,
              };
              changed = true;
            });

            nextInventoryCounts[entity] = nextEntityCounts;
          });

          if (!changed) return prevState;
          return {
            ...prevState,
            inventoryCounts: nextInventoryCounts,
          };
        });

        const currentOrganisationId = k.getState().userContext.organisationId;
        touchedCountEntities.forEach((entity) => {
          void fetchInventoryCount(entity, {
            force: true,
            organisationId: currentOrganisationId,
          }).catch(() => undefined);
        });
      }

      touchedCountEntities.forEach((entity) => {
        const keyPrefix = `${entity}:`;
        Array.from(inflightCountFetchesRef.current.keys()).forEach((key) => {
          if (key.startsWith(keyPrefix)) {
            inflightCountFetchesRef.current.delete(key);
          }
        });
      });
    }

    if (touchedStatsEntities.size > 0) {
      touchedStatsEntities.forEach((entity) => {
        markInventoryStatsStale(entity);
      });
    }

    // --- Degraded-mode safety net (non-blocking, targeted) ---
    // Socket events should remain the primary sync channel.
    // This async fallback only heals potential event delivery gaps.
    setTimeout(() => {
      // This GET is unordered with the write queue, so its answer can land after a later echo and undo
      // it. While the connection the write was sent on still holds, its echo was delivered and is the sync.
      // ponytail: a row version on every Studio echo (membership patches included) would let any read be
      // dropped when older, replacing this guard and the resync's echo watch (useProjects.ts).
      const echoDelivered = socketConnectionAtSend !== null && kernel.current?.getSocketConnectionId() === socketConnectionAtSend;
      results.forEach((result) => {
        const entityType = result.entity as InventoryEntityType;
        const entityId = typeof result.id === 'string' ? result.id.trim() : '';
        if (!entityId || !INVENTORY_ENDPOINTS[entityType]) {
          return;
        }

        if (result.action === 'DELETE') {
          cancelAutosaveScopes(entityType as KernelEntityType, entityId);
          setEntityDetailCoverage(entityType as KernelEntityType, entityId, null);

          kernel.current?.setInventorySlice(entityType, (prev) => {
            const registry = { ...prev.registry };
            if (!registry[entityId]) {
              return prev;
            }
            delete registry[entityId];

            const { queryCache } = applyEventToQueryCache(prev.queryCache, {
              entityType,
              entityId,
              eventType: 'DELETE',
            });

            return {
              ...prev,
              registry,
              queryCache,
              lastActivity: Date.now(),
            };
          });
          reconcileTouchedSlice(entityType, [entityId], { eventType: 'DELETE' });
          return;
        }
        if (echoDelivered) return;

        if (result.action === 'CREATE' || result.action === 'UPDATE') {
          // syncEntity below only writes the registry: invalidate the cache first so a lost socket
          // event cannot leave workspace/project layer lists with stale membership.
          const eventType = result.action === 'CREATE' ? 'CREATE' : 'UPDATE';
          kernel.current?.setInventorySlice(entityType, (prev) => {
            const { queryCache, requeryLists } = applyEventToQueryCache(prev.queryCache, {
              entityType,
              entityId,
              eventType,
            });
            if (!requeryLists) return prev;
            return { ...prev, queryCache, lastActivity: Date.now(), lastCreateActivity: Date.now() };
          });
        }

        kernel.current?.syncEntity(entityType, entityId).catch((error) => {
          console.warn(`[Mutation Fallback] Failed to sync ${entityType}:${entityId}`, error);
        });
      });
    }, 0);
  }, [cancelAutosaveScopes, kernel, markInventoryStatsStale, reconcileTouchedSlice, setEntityDetailCoverage]);

  const performMutationRequest = useCallback(async (
    operations: InventoryMutationOperation[],
    reason: string,
    signal?: AbortSignal,
    keepalive = false,
  ): Promise<InventoryMutationSuccess[]> => {
    const activeChatSessionIdRaw = kernel.current?.getActiveChatSessionId?.() ?? null;
    const activeChatSessionId =
      typeof activeChatSessionIdRaw === 'string' ? activeChatSessionIdRaw.trim() : '';
    const activeSessionEntity =
      activeChatSessionId &&
      kernel.current?.getInventory?.().ChatSession.registry?.[activeChatSessionId]
        ? kernel.current.getInventory().ChatSession.registry[activeChatSessionId]
        : null;
    const activeSessionOwnerId =
      typeof activeSessionEntity?.ownerId === 'string' ? activeSessionEntity.ownerId.trim() : '';
    const requesterUserIdRaw = kernel.current?.getUserContext?.().dbUserId ?? null;
    const requesterUserId =
      typeof requesterUserIdRaw === 'string' ? requesterUserIdRaw.trim() : '';
    const createsOnlySessions = operations.length > 0 && operations.every((operation) => operation.action === 'CREATE' && operation.entity === 'ChatSession');
    const canAttachActiveChatSessionId = Boolean(
      !createsOnlySessions &&
      activeChatSessionId &&
      activeSessionEntity &&
      (
        activeSessionOwnerId.length === 0 ||
        (requesterUserId.length > 0 && activeSessionOwnerId === requesterUserId)
      )
    );
    const response = await fetchApi('/api/inventory/mutate', {
      method: 'POST',
      signal,
      ...(keepalive ? { keepalive } : {}),
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        operations,
        reason,
        ...(canAttachActiveChatSessionId ? { chatSessionId: activeChatSessionId } : {}),
      }),
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      const backendError = payload?.error;
      if (backendError && typeof backendError === 'object') {
        const index =
          typeof (backendError as { index?: unknown }).index === 'number'
            ? (backendError as { index: number }).index
            : null;
        const action =
          typeof (backendError as { action?: unknown }).action === 'string'
            ? (backendError as { action: string }).action
            : 'UNKNOWN_ACTION';
        const entity =
          typeof (backendError as { entity?: unknown }).entity === 'string'
            ? (backendError as { entity: string }).entity
            : 'UNKNOWN_ENTITY';
        const message =
          typeof (backendError as { message?: unknown }).message === 'string'
            ? (backendError as { message: string }).message
            : 'Mutation failed.';

        throw Object.assign(new Error(
          index !== null
            ? `Mutation failed at operation ${index} (${action} ${entity}): ${message}`
            : `Mutation failed (${action} ${entity}): ${message}`,
        ), {
          mutationFailure: {
            ...(index !== null ? { index } : {}),
            entity,
            action,
            message,
            refused: (backendError as { refused?: unknown }).refused === true,
            transient: (backendError as { transient?: unknown }).transient === true,
          },
        });
      }
      throw Object.assign(new Error(payload?.message || `Mutation failed (HTTP ${response.status}).`), { status: response.status });
    }

    if (!payload || !payload.success) {
      throw new Error(payload?.error?.message || payload?.message || 'Mutation failed.');
    }

    return payload.results as InventoryMutationSuccess[];
  }, [kernel]);

  // An answered layer CREATE is its row, entered as its echo would be: a later write is an UPDATE, a DELETE removes it.
  const adoptCreatedStudioLayers = useCallback((operations: InventoryMutationOperation[]) => {
    const registry = kernel.current?.getState().inventory.StudioLayer.registry ?? {};
    // Studio writes carry the row's id, which the operation types leave out.
    const created = (operations as Array<{ entity: string; action: string; id?: string; payload?: unknown }>)
      .filter((operation) => operation.entity === 'StudioLayer' && operation.action === 'CREATE' && operation.id && !registry[operation.id]);
    if (created.length > 0) {
      applyInventoryEvents(created.map((operation) => ({ entityType: 'StudioLayer', entityId: operation.id!, eventType: 'CREATE', payload: operation.payload }) as unknown as RealtimeEvent));
    }
  }, [applyInventoryEvents, kernel]);

  const flushAutosaveMutationBatch = useCallback(async (): Promise<void> => {
    if (autosaveMutationBatchInFlightRef.current || studioWriteSuspendedSinceRef.current !== null) return;
    if (autosaveMutationBatchTimerRef.current) {
      clearTimeout(autosaveMutationBatchTimerRef.current);
      autosaveMutationBatchTimerRef.current = null;
    }

    const queue = autosaveMutationBatchQueueRef.current;
    if (queue.length === 0) return;
    // A leaving page sends what a keepalive request can carry; a head too large for one leaves as an
    // ordinary request, which the page may not outlive.
    const keepaliveCount = studioWriteLeaveRef.current ? countStudioWriteBatch(queue, STUDIO_WRITE_KEEPALIVE_BODY_LIMIT_BYTES) : 0;
    const pendingBatch = queue.splice(0, keepaliveCount || countStudioWriteBatch(queue));

    autosaveMutationBatchInFlightRef.current = true;
    const epoch = clearEpochRef.current;
    let unsettled = pendingBatch;
    let retryDelayMs: number | null = null;
    const refusedLabels = new Set<string>();
    try {
      // The server rolls the whole request back on one refused operation: the save carrying it is
      // set aside and the others are sent again. Each round settles the batch, removes one save, or
      // puts the batch back at the head of the queue to be sent again after a wait.
      while (unsettled.length > 0) {
        // Suspended since the last round: the saves left to send again wait at the head.
        if (studioWriteSuspendedSinceRef.current !== null) {
          autosaveMutationBatchQueueRef.current.unshift(...unsettled);
          unsettled = [];
          break;
        }
        const operations = unsettled.flatMap((entry) => entry.operations);
        const reason = `${STUDIO_AUTOSAVE_BATCH_REASON_PREFIX} (${unsettled.length} requests, ${operations.length} ops)`;
        let combinedResults: InventoryMutationSuccess[];
        const socketConnectionAtSend = kernel.current?.getSocketConnectionId() ?? null;
        const limit = new AbortController();
        const limitTimer = setTimeout(() => limit.abort(), STUDIO_WRITE_REQUEST_LIMIT_MS);
        try {
          combinedResults = await performMutationRequest(operations, reason, limit.signal, keepaliveCount > 0);
        } catch (error) {
          // The inventory was cleared meanwhile and the queue with it: what was in flight goes too.
          if (clearEpochRef.current !== epoch) throw error;
          // A 4xx without a failure body is the server's answer to the request as a whole, and would
          // be the same again; 401, 408 and 429 pass with time, so they are retried like a fault.
          const status = readMutationFailure(error) ? undefined : (error as { status?: number } | null)?.status;
          if (status && status >= 400 && status < 500 && ![401, 408, 429].includes(status)) {
            unsettled.forEach((entry) => entry.operations.forEach((operation) => {
              refusedLabels.add(STUDIO_AUTOSAVE_ENTITY_LABELS[operation.entity] ?? 'project');
            }));
            throw error;
          }
          const failedEntry = findFailedAutosaveBatchEntry(unsettled, error);
          // A fault is sent again as is, behind nothing: a later write must not overtake an earlier
          // one. An operation that keeps faulting on its own is set aside like a refused one, or it
          // would block the queue for good.
          autosaveMutationBatchStallsRef.current = failedEntry ? 0 : autosaveMutationBatchStallsRef.current + 1;
          if (failedEntry) failedEntry.faults = (failedEntry.faults ?? 0) + 1;
          // The workspace this save belongs to never reached the server (its create was lost), yet the
          // browser holds it: it is created again from that copy and the save follows it, once. Left
          // set aside, every later save of that workspace was refused for good (2026-10-15).
          const missingWorkspaceId = failedEntry && !failedEntry.followedMissingWorkspace ? missingWorkspaceIdOf(error) : null;
          const missingWorkspace = missingWorkspaceId ? kernel.current?.getWorkspace(missingWorkspaceId) : null;
          if (failedEntry && missingWorkspaceId && missingWorkspace?.projectId) {
            unsettled = unsettled.filter((entry) => entry !== failedEntry);
            failedEntry.followedMissingWorkspace = true;
            if (!recreatedWorkspaceIdsRef.current.has(missingWorkspaceId)) {
              recreatedWorkspaceIdsRef.current.add(missingWorkspaceId);
              void kernel.current?.persistWorkspaceCreate(missingWorkspace, 'useInventory.recreateMissingWorkspace').catch(() => undefined);
            }
            autosaveMutationBatchQueueRef.current.push(failedEntry);
            continue;
          }
          if (failedEntry && (readMutationFailure(error)?.refused === true || failedEntry.faults! > STUDIO_WRITE_RETRY_DELAYS_MS.length)) {
            unsettled = unsettled.filter((entry) => entry !== failedEntry);
            refusedLabels.add(STUDIO_AUTOSAVE_ENTITY_LABELS[readMutationFailure(error)?.entity ?? ''] ?? 'project');
            failedEntry.reject(Object.assign(error as Error, { studioWriteSetAside: true }));
            continue;
          }
          const faultCount = failedEntry?.faults ?? autosaveMutationBatchStallsRef.current;
          retryDelayMs = STUDIO_WRITE_RETRY_DELAYS_MS[faultCount - 1] ?? STUDIO_WRITE_HELD_RETRY_MS;
          if (!failedEntry && faultCount === STUDIO_WRITE_RETRY_DELAYS_MS.length + 1) {
            raiseStudioWriteHeldNotice(kernel.current);
          }
          // Back at the head: the writes queued meanwhile leave after these.
          autosaveMutationBatchQueueRef.current.unshift(...unsettled);
          unsettled = [];
          break;
        } finally {
          clearTimeout(limitTimer);
        }

        if (combinedResults.length !== operations.length) {
          throw new Error(
            `Batched autosave mutation mismatch: expected ${operations.length} results, got ${combinedResults.length}.`
          );
        }

        let cursor = 0;
        unsettled.forEach((entry) => {
          const operationCount = entry.operations.length;
          const slicedResults = combinedResults.slice(cursor, cursor + operationCount);
          cursor += operationCount;
          applyMutationResultsFallback(slicedResults, socketConnectionAtSend);
          adoptCreatedStudioLayers(entry.operations);
          entry.resolve(slicedResults);
        });
        unsettled = [];
      }
    } catch (error) {
      unsettled.forEach((entry) => {
        entry.reject(error);
      });
    } finally {
      // After a clear, the flag and the timer belong to whatever was queued since.
      if (clearEpochRef.current === epoch) {
        // Kept through a retry wait, so a new write queues behind it instead of sending at once;
        // released once held, where a new write is itself a reason to try.
        // Any answer of the server ends a hold, an answer about one operation included. Not while an
        // identity switch suspends the queue: what waits behind this answer is still not leaving.
        if (studioWriteSuspendedSinceRef.current === null && (retryDelayMs === null || autosaveMutationBatchStallsRef.current === 0)) toast.dismiss('studio-write-held');
        if (retryDelayMs === null) autosaveMutationBatchStallsRef.current = 0;
        autosaveMutationBatchInFlightRef.current = retryDelayMs !== null && !isStudioWriteQueueHeld(autosaveMutationBatchStallsRef.current);
        if (autosaveMutationBatchQueueRef.current.length > 0) {
          autosaveMutationBatchTimerRef.current = setTimeout(() => {
            autosaveMutationBatchTimerRef.current = null;
            autosaveMutationBatchInFlightRef.current = false;
            void flushAutosaveMutationBatch();
          }, retryDelayMs ?? 0);
        }
      }
      if (refusedLabels.size > 0) {
        // One id: the refusals of a flush, and of the flushes that follow, are one notice.
        kernel.current?.handleError(`A change to ${new Intl.ListFormat('en', { type: 'conjunction' }).format([...refusedLabels].map((label) => `a ${label}`))} could not be saved.`, { id: 'studio-autosave-refused' });
      }
    }
  }, [adoptCreatedStudioLayers, applyMutationResultsFallback, kernel, performMutationRequest]);

  const scheduleAutosaveMutationBatchFlush = useCallback((delayMs: number) => {
    if (autosaveMutationBatchTimerRef.current) return;
    autosaveMutationBatchTimerRef.current = setTimeout(() => {
      autosaveMutationBatchTimerRef.current = null;
      void flushAutosaveMutationBatch();
    }, Math.max(0, delayMs));
  }, [flushAutosaveMutationBatch]);

  const requestFactoryChange: useInventoryReturnType['requestFactoryChange'] = useCallback(async (request) => {
    const k = kernel.current;
    if (!k || !request.userId || k.getState().userContext.dbUserId !== request.userId) throw new Error('Your account changed. Reopen Factory before continuing.');
    if ((request.organisationId === undefined) === (request.workspaceId === undefined) || !(request.organisationId ?? request.workspaceId)?.trim()) throw new Error('Choose exactly one Factory scope.');
    const query = request.organisationId !== undefined ? `organisationId=${encodeURIComponent(request.organisationId)}` : `workspaceId=${encodeURIComponent(request.workspaceId)}`;
    const response = await fetch(`/api/list/factory-${request.resource}` + (request.method === 'GET' ? `?${query}` : ''), request.method === 'GET' ? { method: 'GET', cache: 'no-store', signal: request.signal } : {
      method: request.method, headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...request.payload, organisationId: request.organisationId, workspaceId: request.workspaceId }),
    });
    const body = await response.json().catch(() => null);
    const currentKernel = kernel.current;
    if (!currentKernel || currentKernel.getState().userContext.dbUserId !== request.userId) throw new Error('Your account changed. Reopen Factory to see the result.');
    if (!response.ok) throw Object.assign(new Error(body?.message || (request.method === 'GET' ? 'Personas could not be loaded. Try again.' : 'Factory could not save this change. Your draft is kept.')), { status: response.status });
    if (request.method === 'GET') {
      if (!Array.isArray(body?.[request.resource]) || !Number.isSafeInteger(request.resource === 'archetypes' ? body?.factoryProfileRevision : body?.revision)) throw new Error('Factory references could not be loaded. Try again.');
    } else currentKernel.logUserAction('Factory change', { resource: request.resource, method: request.method, organisationId: request.organisationId, workspaceId: request.workspaceId });
    return body;
  }, [kernel]);

  const hasUnsentStudioWrites = useCallback((): boolean =>
    autosaveMutationBatchQueueRef.current.length > 0 || studioWritesPreparingRef.current > 0 || [...autosaveQueueRef.current.values()].some(isUnsentStudioAutosave), []);

  // A suspension that lasts stops being silent: nothing is sent still, but the user is told, here and
  // at a page leave.
  const hasOverdueSuspendedStudioWrites = useCallback((): boolean => {
    const since = studioWriteSuspendedSinceRef.current;
    return since !== null && Date.now() - since >= STUDIO_WRITE_SUSPENSION_LIMIT_MS && hasUnsentStudioWrites();
  }, [hasUnsentStudioWrites]);

  // The way a Studio write leaves the browser, the sites kernel/studioWriteGuard.test.ts allows by
  // name excepted: entries go out in the order they were queued, one request at a time. A write sent
  // beside this queue can reach the server before one queued earlier.
  // It leaves at once unless a request is in flight or waiting to be sent again after a fault; only
  // the debounced engine saves ask for the window, which exists to merge them.
  const queueStudioWrite = useCallback<useInventoryReturnType['queueStudioWrite']>((operations, options) =>
    new Promise<InventoryMutationSuccess[]>((resolve, reject) => {
      autosaveMutationBatchQueueRef.current.push({
        operations,
        resolve,
        reject,
      });
      if (hasOverdueSuspendedStudioWrites()) raiseStudioWriteHeldNotice(kernel.current);
      if (autosaveMutationBatchInFlightRef.current) return;

      const queuedOperationCount = autosaveMutationBatchQueueRef.current.reduce(
        (sum, entry) => sum + entry.operations.length,
        0
      );
      if (options?.batch && queuedOperationCount < AUTOSAVE_MUTATION_BATCH_MAX_OPERATIONS && !isStudioWriteQueueHeld(autosaveMutationBatchStallsRef.current)) {
        scheduleAutosaveMutationBatchFlush(AUTOSAVE_MUTATION_BATCH_WINDOW_MS);
      } else {
        void flushAutosaveMutationBatch();
      }
    }), [flushAutosaveMutationBatch, hasOverdueSuspendedStudioWrites, kernel, scheduleAutosaveMutationBatchFlush]);

  const resumeStudioWrites = useCallback<useInventoryReturnType['resumeStudioWrites']>(() => {
    if (isStudioWriteQueueHeld(autosaveMutationBatchStallsRef.current)) void flushAutosaveMutationBatch();
  }, [flushAutosaveMutationBatch]);

  // A timer is a wait (the window, a retry, a hold), never an open request: the flag a retry wait
  // holds is released, so the next flush sends now.
  const endStudioWriteWait = useCallback(() => {
    if (autosaveMutationBatchTimerRef.current) autosaveMutationBatchInFlightRef.current = false;
  }, []);

  // The engine's debounce and the queue's waits are skipped, and the request is the queue's own: it
  // is settled as any other when the page survives. A request already open is left to its answer,
  // so what is queued behind it cannot leave; nor can what a keepalive request cannot carry.
  // During an identity switch nothing leaves, and the switch's own reload asks nothing.
  const sendStudioWritesAtLeave = useCallback<useInventoryReturnType['sendStudioWritesAtLeave']>(() => {
    if (studioWriteSuspendedSinceRef.current !== null) return hasOverdueSuspendedStudioWrites();
    studioWriteLeaveRef.current = true;
    try {
      const stayed = handOverStudioAutosaves();
      const queue = autosaveMutationBatchQueueRef.current;
      endStudioWriteWait();
      const waiting = queue.length > 0;
      const open = autosaveMutationBatchInFlightRef.current;
      const cannotLeave = stayed
        || (autosaveMutationBatchStallsRef.current > 0 && (waiting || open))
        || (waiting && (open || countStudioWriteBatch(queue, STUDIO_WRITE_KEEPALIVE_BODY_LIMIT_BYTES) < queue.length));
      void flushAutosaveMutationBatch();
      return cannotLeave;
    } finally {
      studioWriteLeaveRef.current = false;
    }
  }, [endStudioWriteWait, flushAutosaveMutationBatch, handOverStudioAutosaves, hasOverdueSuspendedStudioWrites]);

  // Sends what the outgoing identity wrote while its session still is the server's, then suspends
  // the queue: after the switch an unsent write could only leave under the next identity. Nothing is
  // dropped here, the switch may still fail. Never throws: its caller must reach the switch.
  const drainAndSuspendStudioWrites = useCallback(async (): Promise<void> => {
    try {
      const deadline = Date.now() + STUDIO_WRITE_DRAIN_LIMIT_MS;
      // What is queued is tried now, a held queue included.
      endStudioWriteWait();
      handOverStudioAutosaves();
      void flushAutosaveMutationBatch();
      for (;;) {
        // Each round: an engine save answered meanwhile frees the payload that waited behind it.
        const stayed = handOverStudioAutosaves();
        const open = autosaveMutationBatchInFlightRef.current;
        if (!stayed && autosaveMutationBatchQueueRef.current.length === 0 && !open) break;
        if (Date.now() >= deadline || (!open && isStudioWriteQueueHeld(autosaveMutationBatchStallsRef.current))) break;
        await new Promise<void>((resolve) => setTimeout(resolve, STUDIO_WRITE_DRAIN_POLL_MS));
      }
    } catch (error) {
      console.error('[Inventory] Studio writes could not be drained before the identity switch.', error);
    } finally {
      const since = Date.now();
      studioWriteSuspendedSinceRef.current = since;
      setTimeout(() => {
        if (studioWriteSuspendedSinceRef.current === since && hasOverdueSuspendedStudioWrites()) raiseStudioWriteHeldNotice(kernel.current);
      }, STUDIO_WRITE_SUSPENSION_LIMIT_MS);
    }
  }, [endStudioWriteWait, flushAutosaveMutationBatch, handOverStudioAutosaves, hasOverdueSuspendedStudioWrites, kernel]);

  // No limit on `perform`: a switch request given up here may still have happened on the server.
  const runIdentitySwitch = useCallback<useInventoryReturnType['runIdentitySwitch']>(async (perform, failed) => {
    await drainAndSuspendStudioWrites();
    let switched = false;
    try {
      const outcome = await perform();
      switched = !failed?.(outcome);
      return outcome;
    } finally {
      // Already lifted when the inventory was cleared meanwhile.
      if (studioWriteSuspendedSinceRef.current !== null) {
        if (!switched) {
          // The identity is unchanged: its writes are sent again. A timer still armed sends them itself.
          studioWriteSuspendedSinceRef.current = null;
          // Nothing left to send means no answer will come to take the notice down.
          if (!hasUnsentStudioWrites() && !autosaveMutationBatchInFlightRef.current) toast.dismiss('studio-write-held');
          if (!autosaveMutationBatchTimerRef.current) void flushAutosaveMutationBatch();
        } else if (hasUnsentStudioWrites()) {
          // They go with the clear that follows. A notice that fails must not undo the switch for the caller.
          try {
            kernel.current?.handleError('Your latest changes could not be saved before leaving this account.');
          } catch (error) {
            console.error('[Inventory] The unsaved changes notice could not be raised.', error);
          }
        }
      }
    }
  }, [drainAndSuspendStudioWrites, flushAutosaveMutationBatch, hasUnsentStudioWrites, kernel]);

  const mutateInventory = useCallback(async (operations: InventoryMutationOperation[], reason: string, signal?: AbortSignal) => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel unavailable');

    const shortlistActions = (['Lead', 'PortfolioFit'] as const).flatMap((entity) =>
      (['INTERESTING', 'DISMISSED'] as const).flatMap((status) => {
        const indices = operations.flatMap((operation, index) =>
          operation.action === 'UPDATE' && operation.entity === entity && operation.payload.status === status ? [index] : []);
        if (!indices.length) return [];
        const inventory = k.getInventory?.();
        const ids = indices.map((index) => { const operation = operations[index]!; return 'id' in operation && typeof operation.id === 'string' ? operation.id : undefined; });
        const campaigns = ids.map((id) => {
          const row = id ? inventory?.[entity].registry[id] : undefined;
          return row && ('lastCampaignId' in row ? row.lastCampaignId : 'campaignId' in row ? row.campaignId : undefined);
        });
        const campaignId = typeof campaigns[0] === 'string' && campaigns.every((id) => id === campaigns[0]) ? campaigns[0] : undefined;
        return [{ indices, entity, complete: k.beginSourceAction?.({ action: 'shortlist', origin: 'unknown', data: { entityType: entity, entityId: ids.length === 1 ? ids[0] : undefined, campaignId, status, count: indices.length } }) }];
      }));
    try {
      const results = await performMutationRequest(operations, reason, signal);
      for (const action of shortlistActions) {
        if (Array.isArray(results) && action.indices.every((index) => results.some((result) =>
          result?.status === 'SUCCESS' && result.index === index && result.entity === action.entity && result.action === 'UPDATE' && typeof result.id === 'string' && result.id.length > 0))) {
          action.complete?.('client_returned');
        }
      }
      applyMutationResultsFallback(results);
      return results;
    } catch (error) {
      for (const action of shortlistActions) action.complete?.(sourceActionFailure(error));
      throw error;
    }
  }, [
    applyMutationResultsFallback,
    kernel,
    performMutationRequest,
  ]);

  const geocodePropertyAsset = useCallback(async (
    propertyId: string,
    payload?: {
      address?: string;
      city?: string;
      country?: string;
      forceAddressUpdate?: boolean;
    }
  ): Promise<LeasingPropertyDTO | null> => {
    const normalizedPropertyId = typeof propertyId === 'string' ? propertyId.trim() : '';
    if (!normalizedPropertyId) {
      throw new Error('Missing property id.');
    }

    const response = await fetch(
      `/api/inventory/properties/${encodeURIComponent(normalizedPropertyId)}/geocode`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload ?? {}),
      }
    );
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(
        (body && typeof body.message === 'string' && body.message) ||
          `Geocoding failed (HTTP ${response.status}).`
      );
    }

    const synced = await syncEntity('Property', normalizedPropertyId);
    return synced as LeasingPropertyDTO | null;
  }, [syncEntity]);

  const uploadBrandLogo = useCallback(async (
    brandId: string,
    file: File
  ): Promise<LeasingBrandDTO | null> => {
    const normalizedBrandId = typeof brandId === 'string' ? brandId.trim() : '';
    if (!normalizedBrandId) {
      throw new Error('Missing brand id.');
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(
      `/api/inventory/brands/${encodeURIComponent(normalizedBrandId)}/logo`,
      { method: 'POST', body: formData }
    );
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(
        (body && typeof body.message === 'string' && body.message) ||
          `Logo upload failed (HTTP ${response.status}).`
      );
    }

    const synced = await syncEntity('Brand', normalizedBrandId);
    return synced as LeasingBrandDTO | null;
  }, [syncEntity]);

  // Pas de syncEntity ici : persistFloorPlanAsset emet deja l'evenement temps reel de mutation,
  // le Floor se remet a jour tout seul cote client.
  const uploadFloorPlan = useCallback(async (
    floorId: string,
    file: File
  ): Promise<void> => {
    const normalizedFloorId = typeof floorId === 'string' ? floorId.trim() : '';
    if (!normalizedFloorId) {
      throw new Error('Missing floor id.');
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(
      `/api/inventory/floors/${encodeURIComponent(normalizedFloorId)}/plan`,
      { method: 'POST', body: formData }
    );
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(
        (body && typeof body.message === 'string' && body.message) ||
          `Floor plan upload failed (HTTP ${response.status}).`
      );
    }
  }, []);

  const createEntity = useCallback(async <K extends InventoryEntityType>(type: K, payload: Partial<InventoryRegistry[K]['dto']>) => {
    const results = await mutateInventory([{
      action: 'CREATE',
      entity: type as any,
      payload: payload as any
    }], `Create ${type}`);
    const result = results[0];
    if (!result) throw new Error(`Failed to get result for ${type} creation`);
    toast.success(`${type} created`);
    return result;
  }, [mutateInventory]);

  // A Fater admin stamps a brand by hand (lib/inventory/brands/faterStampDecisions); the row shows
  // the decision at once, and every list reads it from the server on its next query.
  const setBrandFaterStamp = useCallback(async (brandId: string, stamped: boolean) => {
    const response = await fetch(`/api/inventory/brands/${encodeURIComponent(brandId)}/stamp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stamped }),
    });
    const body = await response.json().catch(() => null);
    if (!response.ok) throw new Error((body && typeof body.message === 'string' && body.message) || `Stamp failed (HTTP ${response.status}).`);
    kernel.current?.setInventorySlice('Brand', (prev) => {
      const brand = prev.registry[brandId];
      const stamp = brand?.qualification?.stamp;
      if (!brand || !stamp) return prev;
      return { ...prev, registry: { ...prev.registry, [brandId]: { ...brand, qualification: { ...brand.qualification!, stamp: { ...stamp, recommended: stamped } } } } };
    });
  }, [kernel]);

  const requestBrandEvaluationRefresh = useCallback(async () => {
    const response = await fetch('/api/brands/evaluation', { method: 'POST' });
    const body = await response.json().catch(() => null);
    if (!response.ok) throw new Error((body && typeof body.message === 'string' && body.message) || `Recalculation failed (HTTP ${response.status}).`);
  }, []);

  const updateEntity = useCallback(async <K extends InventoryEntityType>(type: K, id: string, payload: Partial<InventoryRegistry[K]['dto']>) => {
    if (type === 'User') {
      const phoneNumber = (payload as Partial<LeasingUserDTO>).phoneNumber;
      if (typeof phoneNumber !== 'string') {
        throw new Error('User updates currently require a phone number.');
      }
      const response = await fetch('/api/user/phone', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber }),
      });
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(
          (body && typeof body.message === 'string' && body.message)
          || `User update failed (HTTP ${response.status}).`
        );
      }
      await syncEntity('User', id, { force: true });
      return {
        status: 'SUCCESS',
        index: 0,
        entity: 'User' as any,
        action: 'UPDATE',
        id,
      } as InventoryMutationSuccess;
    }

    if (type === 'Notification') {
      const ownerId = kernel.current?.getUserContext().dbUserId;
      const requestEpoch = clearEpochRef.current;
      if (!ownerId) throw new Error('Sign in to update notifications.');
      const response = await fetch(`/api/inventory/notifications/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload ?? {}),
      });
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(
          (body && typeof body.message === 'string' && body.message) ||
          `Notification update failed (HTTP ${response.status}).`
        );
      }
      if (requestEpoch === clearEpochRef.current && kernel.current?.getUserContext().dbUserId === ownerId && body?.entity?.userId === ownerId) {
        hydrateInventoryData({ notifications: [body.entity as NotificationDTO] });
      }
      return {
        status: 'SUCCESS',
        index: 0,
        entity: 'Notification' as any,
        action: 'UPDATE',
        id,
      } as InventoryMutationSuccess;
    }

    const results = await mutateInventory([{
      action: 'UPDATE',
      entity: type as any,
      id,
      payload: payload as any
    }], `Update ${type} ${id}`);
    const result = results[0];
    if (!result) throw new Error(`Failed to get result for ${type} update`);
    const payloadRecord = (payload ?? {}) as Record<string, unknown>;
    const isProjectTrashToggle =
      type === 'Project' && Object.prototype.hasOwnProperty.call(payloadRecord, 'toDelete');
    if (!isProjectTrashToggle) {
      toast.success(`${type} updated`);
    }
    return result;
  }, [hydrateInventoryData, mutateInventory, syncEntity]);

  const deleteEntity = useCallback(async <K extends InventoryEntityType>(type: K, id: string) => {
    const results = await mutateInventory([{
      action: 'DELETE',
      entity: type as any,
      id
    }], `Delete ${type} ${id}`);
    const result = results[0];
    if (!result) throw new Error(`Failed to get result for ${type} deletion`);
    if (type !== 'Project') {
      toast.success(`${type} deleted`);
    }
    return result;
  }, [mutateInventory]);

  return {
    applyInventoryEvents,
    watchInventoryEchoes,
    readPropertyTrafficEvidence,
    syncEntity,
    ensureFactoryPreset,
    removeFactoryPreset,
    geocodePropertyAsset,
    uploadBrandLogo,
    uploadFloorPlan,
    ensureInventoryIds,
    queueAutosave,
    queueStudioWorkspaceSave,
    markStudioWorkspaceFieldsSent,
    flushAutosave,
    awaitAutosaveInFlight,
    cancelAutosave,
    settleAutosave,
    getAutosaveState: readAutosaveState,
    getUnobservedAutosaveValues,
    setAutosaveBaseline,
    refreshInventory,
    queryInventory,
    loadOlderTimelineEvents,
    fetchInventoryCount,
    fetchInventoryStats,
    invalidateInventoryStats,
    initializeInventory,
    reconnectInventory,
    clearInventory,
    mutateInventory,
    queueStudioWrite,
    prepareStudioWrite,
    resumeStudioWrites,
    sendStudioWritesAtLeave,
    runIdentitySwitch,
    requestFactoryChange,
    requestDaimonConversation,
    requestDaimonTool,
    requestModelist,
    requestVideo,
    requestShare,
    requestSign,
    loadFater3DSites,
    renameFater3DSite,
    deleteFater3DSite,
    createEntity,
    updateEntity,
    setBrandFaterStamp,
    requestBrandEvaluationRefresh,
    deleteEntity
  };
};
